import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";
import {
  ReloadOutlined,
  SyncOutlined,
  CloseOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  BarChartOutlined,
  CloudUploadOutlined,
  ClockCircleOutlined,
  LockOutlined,
  EyeOutlined,
  WarningOutlined,
  FlagOutlined,
  BuildOutlined,
  FilterOutlined,
  DashboardOutlined,
  RiseOutlined,
  FallOutlined,
  ThunderboltOutlined,
  TrophyOutlined,
  UserOutlined,
  PrinterOutlined
} from "@ant-design/icons";
import {
  Select,
  Button,
  Layout,
  Spin,
  Alert,
  Card,
  Tag,
  Typography,
  message,
  Badge,
  Tooltip as AntTooltip,
  Modal,
  Table,
  Space,
  Popover,
  Divider,
  Tabs,
  Empty,
  Row,
  Col,
  Statistic,
  Progress
} from "antd";
import Navbar from "./Navbar";
import "./Dashboard.css";
import axios from "axios";

const { Content } = Layout;
const { Option } = Select;
const { Text } = Typography;
const { TabPane } = Tabs;

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_BASE = `${API_BASE_URL}/api`;

const BREAKPOINT = 768;
const LS_KEY = "dma_sb_collapsed";
const MAHARASHTRA_STATE_ID = 1;

// 🌐 TEXT MAP FOR DASHBOARD
const DASHBOARD_TEXT = {
  EN: {
    dashboardTitle: "Submission Status",
    dashboardSubtitle: "ULB analytics dashboard",
    filters: "Filters",
    autoApply: "(Auto-apply)",
    state: "State",
    division: "Division",
    district: "District",
    ulbs: "ULBs",
    ulbType: "ULB Type",
    ulbClass: "ULB Class",
    forms: "Subject",
    refresh: "Refresh",
    resetAllFilters: "Reset All Filters",
    showingULBs: "Showing {count} ULB(s)",
    showingDistricts: "Showing {count} District(s)",
    slaCompliance: "SLA Breach",
    reports: "Reports",
    totalSubmissions: "Total Submissions",
    pending: "Pending Submissions",
    inProgress: "In Progress",
    completed: "Completed",
    totalForms: "Total Subject",
    loading: "Loading...",
    ulbDetails: "ULB Details",
    ulbName: "ULB Name",
    ulbClass: "Class",
    ulbType: "Type",
    establishmentYear: "Est. Year",
    censusCode: "Census Code",
    noDataAvailable: "No data available",
    noULBsFound: "No ULBs found for selected district",
    failedToLoadULBs: "Failed to load ULBs",
    failedToLoadStats: "Failed to load dashboard statistics",
    selectedULBs: "Selected ULBs",
    removeAll: "Remove All",
    daoApproved: "DAO Approved",
    rdmaApproved: "RDMA Approved",
    selectState: "Select State",
    selectDivision: "Select Division",
    selectDistrict: "Select District first",
    selectULB: "Select ULB",
    selectForm: "Select Form",
    clickForDetails: "Click for details",
    loadingForms: "Loading forms...",
    noFormsAvailable: "No forms available",
    formsCount: "Subject",
    dashboardStats: "Dashboard Statistics",
    percentage: "Percentage",
    divisions: "Divisions",
    districts: "Districts",
    ulbsShort: "ULBs",
    activeForms: "Active Subject",
    totalFields: "Total Fields",
    submissionsReceived: "Submissions Received",
    formDataLoaded: "Form data loaded successfully",
    byDivision: "By Division",
    byDistrict: "By District",
    byULB: "By ULB",
    allDistricts: "All Districts",
    allULBs: "All ULBs",
    selectToViewData: "Select a division or district to view data",
    slaComplianceChart: "SLA Breach",
    slaByDivision: "SLA Breach by Division",
    slaByDistrict: "SLA Breach by District",
    slaByULB: "SLA Breach by ULB",
    singleULBSelected: "Single ULB Selected",
    filterLocked: "Filter locked based on your user profile",
    yourDivision: "Your Division",
    yourDistrict: "Your District",
    yourULB: "Your ULB",
    divisionInfo: "You are viewing data for your division: {division}",
    districtInfo: "You are viewing data for your district: {district}",
    ulbInfo: "You are viewing data for your ULB: {ulb}",
    formsList: "Subject List",
    formName: "Form Name",
    submissionCount: "Submission Count",
    fieldsCount: "Fields Count",
    status: "Status",
    viewDetails: "View Details",
    submissionsList: "Submissions List",
    submittedBy: "Submitted By",
    submittedOn: "Submitted On",
    ulbName: "ULB Name",
    formStatus: "Form Status",
    viewSubmission: "View Submission",
    close: "Close",
    totalFormsCount: "Total Subject: {count}",
    totalSubmissionsCount: "Total Submissions: {count}",
    formsWithSubmissions: "Forms with Submissions",
    allForms: "All Subject",
    submissionsByForm: "Submissions by Form",
    submissionsByULB: "Submissions by ULB",
    noSubmissions: "No submissions found",
    loadingSubmissions: "Loading submissions...",
    slaExcellent: "Below 100%",
    slaGood: "Below 80%",
    slaAverage: "Below 50%",
    slaVeryPoor: "Below 10%",
    viewList: "View List",
    ulbList: "ULB List",
    completionPercentage: "Completion %",
    noUlbsInCategory: "No ULBs in this category",
    totalUlbs: "Total ULBs: {count}",
    slaBreachCounts: "SLA Breach Counts",
    slaViolations: "SLA Violations",
    slaViolationsList: "SLA Violations List",
    stageName: "Stage Name",
    expectedDeadline: "Expected Deadline",
    violatedAt: "Violated At",
    resolvedAt: "Resolved At",
    escalationTo: "Escalated To",
    status: "Status",
    formName: "Form Name",
    submissionId: "Submission ID",
    district: "District",
    division: "Division",
    ulb: "ULB",
    hoursSinceViolation: "Hours Since",
    activeBreach: "Active Breach",
    resolved: "Resolved",
    withinSLA: "Within SLA",
    resolvedLate: "Resolved (Late)",
    resolvedOnTime: "Resolved (On Time)",
    viewViolations: "View Violations",
    noViolations: "No SLA violations found",
    loadingViolations: "Loading violations...",
    slaSummary: "SLA Summary",
    totalViolations: "Total Violations",
    openViolations: "Open Violations",
    resolvedViolations: "Resolved Violations",
    totalWorkflows: "Total Workflows",
    avgResolutionHours: "Avg Resolution (hrs)",
    complianceRate: "Compliance Rate",
    allTypes: "All Types",
    allClasses: "All Classes",
    loadingTypes: "Loading types...",
    loadingClasses: "Loading classes...",
    monthlyTrends: "Monthly Trends",
    submissions: "Submissions",
    breaches: "Breaches",
    selectFormFirst: "Please select a form first"
  },
  MR: {
    dashboardTitle: "सबमिशन स्थिती",
    dashboardSubtitle: "यूएलबी विश्लेषण डॅशबोर्ड",
    filters: "फिल्टर्स",
    autoApply: "(स्वयं-लागू)",
    state: "राज्य",
    division: "विभाग",
    district: "जिल्हा",
    ulbs: "नगरपरिषद",
    ulbType: "प्रकार",
    ulbClass: "वर्ग",
    forms: "विषय",
    refresh: "रिफ्रेश",
    resetAllFilters: "सर्व फिल्टर्स रीसेट करा",
    showingULBs: "{count} यूएलबी दर्शवित आहे",
    showingDistricts: "{count} जिल्हे दर्शवित आहे",
    slaCompliance: "एसएलए भंग",
    reports: "अहवाल",
    totalSubmissions: "एकूण सबमिशन्स",
    pending: "प्रलंबित सबमिशन",
    inProgress: "प्रगतीपथावर",
    completed: "पूर्ण झाले",
    totalForms: "एकूण विषय",
    loading: "लोड होत आहे...",
    ulbDetails: "यूएलबी तपशील",
    ulbName: "यूएलबी नाव",
    ulbClass: "वर्ग",
    ulbType: "प्रकार",
    establishmentYear: "स्थापना वर्ष",
    censusCode: "जनगणना कोड",
    noDataAvailable: "डेटा उपलब्ध नाही",
    noULBsFound: "निवडलेल्या जिल्ह्यासाठी यूएलबी आढळली नाहीत",
    failedToLoadULBs: "यूएलबी लोड करण्यात अयशस्वी",
    failedToLoadStats: "डॅशबोर्ड आकडेवारी लोड करण्यात अयशस्वी",
    selectedULBs: "निवडलेल्या यूएलबी",
    removeAll: "सर्व काढा",
    selectState: "राज्य निवडा",
    selectDivision: "विभाग निवडा",
    selectDistrict: "प्रथम जिल्हा निवडा",
    selectULB: "यूएलबी निवडा",
    daoApproved: "डीएओ मंजूर",
    rdmaApproved: "आरडीएमए मंजूर",
    selectForm: "फॉर्म निवडा",
    clickForDetails: "तपशीलांसाठी क्लिक करा",
    loadingForms: "फॉर्म्स लोड होत आहेत...",
    noFormsAvailable: "फॉर्म्स उपलब्ध नाहीत",
    formsCount: "विषय",
    dashboardStats: "डॅशबोर्ड आकडेवारी",
    percentage: "टक्केवारी",
    divisions: "विभाग",
    districts: "जिल्हे",
    ulbsShort: "यूएलबी",
    activeForms: "सक्रिय विषय",
    totalFields: "एकूण फील्ड्स",
    submissionsReceived: "सबमिशन प्राप्त",
    formDataLoaded: "फॉर्म डेटा यशस्वीरित्या लोड झाला",
    byDivision: "विभागानुसार",
    byDistrict: "जिल्ह्यानुसार",
    byULB: "यूएलबीनुसार",
    allDistricts: "सर्व जिल्हे",
    allULBs: "सर्व यूएलबी",
    selectToViewData: "डेटा पाहण्यासाठी विभाग किंवा जिल्हा निवडा",
    slaComplianceChart: "एसएलए भंग",
    slaByDivision: "विभागानुसार एसएलए भंग",
    slaByDistrict: "जिल्ह्यानुसार एसएलए भंग",
    slaByULB: "यूएलबीनुसार एसएलए भंग",
    singleULBSelected: "एक यूएलबी निवडले",
    filterLocked: "तुमच्या यूजर प्रोफाइलनुसार फिल्टर लॉक केले आहे",
    yourDivision: "तुमचा विभाग",
    yourDistrict: "तुमचा जिल्हा",
    yourULB: "तुमची यूएलबी",
    divisionInfo: "तुम्ही तुमच्या विभागाचा डेटा पाहत आहात: {division}",
    districtInfo: "तुम्ही तुमच्या जिल्ह्याचा डेटा पाहत आहात: {district}",
    ulbInfo: "तुम्ही तुमच्या यूएलबीचा डेटा पाहत आहात: {ulb}",
    formsList: "विषयांची यादी",
    formName: "फॉर्मचे नाव",
    submissionCount: "सबमिशन संख्या",
    fieldsCount: "फील्ड्स संख्या",
    status: "स्थिती",
    viewDetails: "तपशील पहा",
    submissionsList: "सबमिशन्सची यादी",
    submittedBy: "सबमिट केले",
    submittedOn: "सबमिट केल्याची तारीख",
    ulbName: "यूएलबीचे नाव",
    formStatus: "फॉर्मची स्थिती",
    viewSubmission: "सबमिशन पहा",
    close: "बंद करा",
    totalFormsCount: "एकूण विषय: {count}",
    totalSubmissionsCount: "एकूण सबमिशन्स: {count}",
    formsWithSubmissions: "सबमिशन असलेले फॉर्म्स",
    allForms: "सर्व विषय",
    submissionsByForm: "फॉर्मनुसार सबमिशन्स",
    submissionsByULB: "यूएलबीनुसार सबमिशन्स",
    noSubmissions: "कोणतेही सबमिशन आढळले नाही",
    loadingSubmissions: "सबमिशन्स लोड होत आहेत...",
    slaExcellent: "१००% पेक्षा कमी",
    slaGood: "८०% पेक्षा कमी",
    slaAverage: "५०% पेक्षा कमी",
    slaVeryPoor: "१०% पेक्षा कमी",
    viewList: "यादी पहा",
    ulbList: "यूएलबी यादी",
    completionPercentage: "पूर्णता टक्केवारी",
    noUlbsInCategory: "या श्रेणीत कोणतीही यूएलबी नाही",
    totalUlbs: "एकूण यूएलबी: {count}",
    slaBreachCounts: "एसएलए भंग संख्या",
    slaViolations: "एसएलए भंग",
    slaViolationsList: "एसएलए भंग यादी",
    stageName: "स्टेजचे नाव",
    expectedDeadline: "अपेक्षित मुदत",
    violatedAt: "भंग झाला",
    resolvedAt: "निराकरण झाले",
    escalationTo: "वाढवले",
    status: "स्थिती",
    formName: "फॉर्मचे नाव",
    submissionId: "सबमिशन आयडी",
    district: "जिल्हा",
    division: "विभाग",
    ulb: "यूएलबी",
    hoursSinceViolation: "तास झाले",
    activeBreach: "सक्रिय भंग",
    resolved: "निराकरण झाले",
    withinSLA: "एसएलए मध्ये",
    resolvedLate: "उशीरा निराकरण",
    resolvedOnTime: "वेळेत निराकरण",
    viewViolations: "भंग पहा",
    noViolations: "कोणतेही एसएलए भंग आढळले नाहीत",
    loadingViolations: "भंग लोड होत आहेत...",
    slaSummary: "एसएलए सारांश",
    totalViolations: "एकूण भंग",
    openViolations: "खुले भंग",
    resolvedViolations: "निराकरण झाले",
    totalWorkflows: "एकूण वर्कफ्लो",
    avgResolutionHours: "सरासरी निराकरण (तास)",
    complianceRate: "अनुपालन दर",
    allTypes: "सर्व प्रकार",
    allClasses: "सर्व वर्ग",
    loadingTypes: "प्रकार लोड होत आहेत...",
    loadingClasses: "वर्ग लोड होत आहेत...",
    monthlyTrends: "मासिक ट्रेंड",
    submissions: "सबमिशन्स",
    breaches: "भंग",
    selectFormFirst: "कृपया प्रथम फॉर्म निवडा"
  }
};

// Update KPI_CONFIG
const KPI_CONFIG = {
  totalForms: {
    id: "totalForms",
    labelEN: "Subject",
    labelMR: "एकूण विषय",
    color: "#1e40af",
    icon: <FileTextOutlined />,
    calculation: (stats) => stats.totalForms,
    apiField: "totalForms"
  },
  totalSubmissions: {
    id: "totalSubmissions",
    labelEN: "Total Submissions",
    labelMR: "एकूण सबमिशन्स",
    color: "#10b981",
    icon: <CloudUploadOutlined />,
    calculation: (stats) => stats.totalSubmissions,
    apiField: "totalSubmissions"
  },
  filledSubmissions: {
    id: "filledSubmissions",
    labelEN: "Submitted by ULB",
    labelMR: "भरलेले",
    color: "#22c55e",
    icon: <CheckCircleOutlined />,
    calculation: (stats) => stats.filledSubmissions,
    apiField: "filledSubmissions"
  },
  draftSubmissions: {
    id: "draftSubmissions",
    labelEN: "Pending ULBs Submission",
    labelMR: "प्रलंबित यूएलबी सबमिशन",
    color: "#f59e0b",
    icon: <ClockCircleOutlined />,
    calculation: (stats) => stats.draftSubmissions,
    apiField: "draftSubmissions"
  },
  daoApproved: {
    id: "daoApproved",
    labelEN: "DAO Approved",
    labelMR: "डीएओ मंजूर",
    color: "#8b5cf6",
    icon: <CheckCircleOutlined />,
    calculation: (stats) => stats.daoApproved || 0,
    apiField: "daoApproved"
  },
  rdmaApproved: {
    id: "rdmaApproved",
    labelEN: "RDMA Approved",
    labelMR: "आरडीएमए मंजूर",
    color: "#ec4899",
    icon: <TrophyOutlined />,
    calculation: (stats) => stats.rdmaApproved || 0,
    apiField: "rdmaApproved"
  },
  dmaApproved: {
    id: "dmaApproved",
    labelEN: "DMA Approved",
    labelMR: "डीएमए मंजूर",
    color: "#3b82f6",
    icon: <CheckCircleOutlined />,
    calculation: (stats) => stats.dmaApproved,
    apiField: "dmaApproved"
  },
  slaCompliance: {
    id: "slaCompliance",
    labelEN: "SLA Breach",
    labelMR: "एसएलए भंग",
    color: "#ef4444",
    icon: <WarningOutlined />,
    calculation: (stats) => stats.slaBreachCount,
    apiField: "slaBreachCount"
  }
};

// Custom debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Animated Number Counter
const AnimatedNumber = ({ value, duration = 800 }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const previousValue = useRef(0);

  useEffect(() => {
    const startValue = previousValue.current;
    const endValue = value;
    if (startValue === endValue) return;

    const stepTime = Math.abs(duration / (endValue - startValue || 1));
    let current = startValue;

    const timer = setInterval(() => {
      current += (endValue > startValue ? 1 : -1);
      if ((endValue > startValue && current >= endValue) || (endValue < startValue && current <= endValue)) {
        setDisplayValue(endValue);
        clearInterval(timer);
      } else {
        setDisplayValue(current);
      }
    }, stepTime);

    previousValue.current = endValue;
    return () => clearInterval(timer);
  }, [value, duration]);

  return <span>{displayValue.toLocaleString()}</span>;
};

// Floating Card Component
const FloatingCard = ({ children, className, style, onClick, delay = 0 }) => {
  return (
    <div
      className={`floating-card ${className || ''}`}
      style={{
        animationDelay: `${delay}s`,
        ...style
      }}
      onClick={onClick}
    >
      {children}
    </div>
  );
};

const Dashboard = () => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= BREAKPOINT);

  // State for filters
  const [division, setDivision] = useState([]);
  const [district, setDistrict] = useState([]);
  const [ulb, setUlb] = useState([]);
  const [ulbType, setUlbType] = useState("all");
  const [ulbClass, setUlbClass] = useState("all");
  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const [cachedSubmissions, setCachedSubmissions] = useState(null);
  const [cachedSubmissionsKey, setCachedSubmissionsKey] = useState('');
  const [cachedViolations, setCachedViolations] = useState(null);
  const [cachedViolationsKey, setCachedViolationsKey] = useState('');

  // Summary Report states
  const [summaryReportModalVisible, setSummaryReportModalVisible] = useState(false);
  const [summaryReportData, setSummaryReportData] = useState({});
  const [selectedFormForSummary, setSelectedFormForSummary] = useState(null);

  // Dynamic options from database
  const [ulbTypeOptions, setUlbTypeOptions] = useState([]);
  const [ulbClassOptions, setUlbClassOptions] = useState([]);
  const [loadingUlbTypes, setLoadingUlbTypes] = useState(false);
  const [loadingUlbClasses, setLoadingUlbClasses] = useState(false);
  const [ulbTypeError, setUlbTypeError] = useState(null);
  const [ulbClassError, setUlbClassError] = useState(null);

  // 🌐 LANGUAGE STATE
  const [language, setLanguage] = useState(() => {
    const saved = sessionStorage.getItem("dma_portal_language");
    return saved && (saved === "EN" || saved === "MR") ? saved : "EN";
  });

  // Modal states
  const [formsModalVisible, setFormsModalVisible] = useState(false);
  const [submissionsModalVisible, setSubmissionsModalVisible] = useState(false);
  const [slaCategoryModalVisible, setSlaCategoryModalVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryUlbs, setCategoryUlbs] = useState([]);
  const [selectedFormForModal, setSelectedFormForModal] = useState(null);
  const [submissionsData, setSubmissionsData] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [submissionsPagination, setSubmissionsPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [allSubmissionsForPrint, setAllSubmissionsForPrint] = useState([]);

  // SLA Violations state
  const [slaViolations, setSlaViolations] = useState([]);
  const [loadingSlaViolations, setLoadingSlaViolations] = useState(false);
  const [slaViolationsModalVisible, setSlaViolationsModalVisible] = useState(false);
  const [slaViolationsPagination, setSlaViolationsPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [allViolationsForPrint, setAllViolationsForPrint] = useState([]);
  const [slaSummary, setSlaSummary] = useState({
    total_violations: 0,
    open_violations: 0,
    resolved_violations: 0,
    total_workflows: 0,
    avg_resolution_hours: 0,
    compliance_by_form: []
  });

  // 🔐 USER DIVISION/DISTRICT/ULB FROM LOGIN
  const [userDivision, setUserDivision] = useState(null);
  const [userDistrict, setUserDistrict] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userDivisionName, setUserDivisionName] = useState("");
  const [userDistrictName, setUserDistrictName] = useState("");

  // Add ULB user states
  const [userUlb, setUserUlb] = useState(null);
  const [userUlbId, setUserUlbId] = useState(null);
  const [userUlbName, setUserUlbName] = useState("");

  const [divisionOptions, setDivisionOptions] = useState([{ id: 0, name: "All Divisions" }]);
  const [districtOptions, setDistrictOptions] = useState([{ id: 0, name: "All Districts" }]);
  const [ulbOptions, setUlbOptions] = useState([{ id: 0, name: "All ULBs" }]);

  // Forms state
  const [forms, setForms] = useState([]);
  const [loadingForms, setLoadingForms] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [selectedFormId, setSelectedFormId] = useState(null);

  // Form statistics
  const [formStats, setFormStats] = useState({
    totalForms: 0,
    activeForms: 0,
    totalFields: 0,
    totalSubmissions: 0,
    lastUpdated: null
  });

  const [loadingDivision, setLoadingDivision] = useState(false);
  const [loadingDistrict, setLoadingDistrict] = useState(false);
  const [loadingUlb, setLoadingUlb] = useState(false);
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);

  const [ulbError, setUlbError] = useState(null);

  // State for selected ULB details
  const [selectedUlbDetails, setSelectedUlbDetails] = useState([]);
  const [loadingUlbDetails, setLoadingUlbDetails] = useState(false);
  const [summaryLoadingStep, setSummaryLoadingStep] = useState('');
  const [summaryProgress, setSummaryProgress] = useState(0);

  // State for selected KPI
  const [selectedKpi, setSelectedKpi] = useState("slaCompliance");

  const [dashboardStats, setDashboardStats] = useState({
    totalForms: 0,
    reports: 0,
    submissionsReceived: 0,
    totalSubmissions: 0,
    filledSubmissions: 0,
    draftSubmissions: 0,
    dmaApproved: 0,
    daoApproved: 0,
    rdmaApproved: 0,
    totalFields: 0,
    slaCompliance: "0%",
    selectedULBsCount: 0,
    activeForms: 0,
    slaBreachCount: 0
  });
  const [loadingStats, setLoadingStats] = useState(false);
  const [isDeskUser, setIsDeskUser] = useState(false);

  // Chart data based on filters
  const [slaChartData, setSlaChartData] = useState([]);
  const [pieChartData, setPieChartData] = useState([]);
  const [trendData, setTrendData] = useState([]);

  // SLA Category counts
  const [slaCategoryCounts, setSlaCategoryCounts] = useState({
    excellent: 0,
    good: 0,
    average: 0,
    veryPoor: 0
  });

  // State for X-axis configuration
  const [xAxisConfig, setXAxisConfig] = useState({
    type: "division",
    data: [],
    label: "Division"
  });

  // All districts and ULBs for chart
  const [allDistrictsForChart, setAllDistrictsForChart] = useState([]);
  const [allUlbsForChart, setAllUlbsForChart] = useState([]);

  // Refs for Select components
  const divisionSelectRef = useRef(null);
  const districtSelectRef = useRef(null);
  const ulbSelectRef = useRef(null);
  const formSelectRef = useRef(null);
  const filterTopRef = useRef(null);

  // Cache refs for names
  const ulbNameCache = useRef(new Map());
  const districtNameCache = useRef(new Map());
  const divisionNameCache = useRef(new Map());
  const userNameCache = useRef(new Map());
  const allUlbsMappingsCache = useRef(new Map()); // NEW: Cache for ULB mappings

  // Debounced values
  const debouncedDivision = useDebounce(division, 800);
  const debouncedDistrict = useDebounce(district, 800);
  const debouncedUlb = useDebounce(ulb, 800);

  // Component mount ref
  const isMounted = useRef(true);

  // Get current language text
  const t = useMemo(() => DASHBOARD_TEXT[language], [language]);

  // Check if user is ULB level
  const isUlbUser = useMemo(() => {
    return !!(userUlb || (userRole && userRole.toLowerCase().includes('ulb')));
  }, [userUlb, userRole]);

  // Generate trend data
  const generateTrendData = useCallback(() => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months.slice(0, 6).map((month, idx) => ({
      month,
      submissions: Math.floor(Math.random() * 200) + 50 + idx * 15,
      breaches: Math.floor(Math.random() * 30) + 5 + idx * 2,
      compliance: Math.floor(Math.random() * 30) + 60 - idx * 2
    }));
  }, []);

  useEffect(() => {
    setTrendData(generateTrendData());
  }, [generateTrendData]);

  // Helper function to get ULB name by ID (cached)
  const getUlbName = useCallback(async (ulbId) => {
    if (!ulbId || ulbId === 0) return 'Unknown';
    if (ulbNameCache.current.has(ulbId)) {
      return ulbNameCache.current.get(ulbId);
    }
    try {
      const response = await axios.get(`${API_BASE}/meta/ulb?id=${ulbId}`, { timeout: 150000});
      let ulbName = 'Unknown';
      if (response.data?.success && response.data.data) {
        ulbName = response.data.data.ulb_name || response.data.data.name || `ULB ${ulbId}`;
      }
      ulbNameCache.current.set(ulbId, ulbName);
      return ulbName;
    } catch (error) {
      console.log(`Could not fetch ULB name for ID ${ulbId}:`, error.message);
      return `ULB ${ulbId}`;
    }
  }, [API_BASE]);

  // Helper function to get District name by ID
  const getDistrictName = useCallback(async (districtId) => {
    if (!districtId || districtId === 0) return 'Unknown';

    if (districtNameCache.current.has(districtId)) {
      return districtNameCache.current.get(districtId);
    }

    const fromOptions = districtOptions.find(d => d.id === districtId);
    if (fromOptions && fromOptions.name && fromOptions.name !== 'All Districts') {
      districtNameCache.current.set(districtId, fromOptions.name);
      return fromOptions.name;
    }

    try {
      const response = await axios.get(`${API_BASE}/meta/districts/${districtId}`, {
        timeout: 8000
      });

      if (response.data?.success && response.data.data) {
        const name = response.data.data.name;
        if (name) {
          districtNameCache.current.set(districtId, name);
          return name;
        }
      }
      return `District ${districtId}`;
    } catch (error) {
      console.log(`Could not fetch district name for ID ${districtId}:`, error.message);
      return `District ${districtId}`;
    }
  }, [API_BASE, districtOptions]);

  // Helper function to get Division name by ID
  const getDivisionName = useCallback(async (divisionId) => {
    if (!divisionId || divisionId === 0) return 'Unknown';

    if (divisionNameCache.current.has(divisionId)) {
      return divisionNameCache.current.get(divisionId);
    }

    const fromOptions = divisionOptions.find(d => d.id === divisionId);
    if (fromOptions && fromOptions.name && fromOptions.name !== 'All Divisions') {
      divisionNameCache.current.set(divisionId, fromOptions.name);
      return fromOptions.name;
    }

    try {
      const response = await axios.get(`${API_BASE}/meta/divisions/${divisionId}`, {
        timeout: 8000
      });

      if (response.data?.success && response.data.data) {
        const name = response.data.data.name;
        if (name) {
          divisionNameCache.current.set(divisionId, name);
          return name;
        }
      }
      return `Division ${divisionId}`;
    } catch (error) {
      console.log(`Could not fetch division name for ID ${divisionId}:`, error.message);
      return `Division ${divisionId}`;
    }
  }, [API_BASE, divisionOptions]);

  // NEW: Fetch all ULBs with district/division mappings (cached)
  const fetchAllUlbsWithMappings = useCallback(async () => {
    if (allUlbsMappingsCache.current.size > 0) {
      console.log(`📦 Using cached ULB mappings (${allUlbsMappingsCache.current.size} ULBs)`);
      return allUlbsMappingsCache.current;
    }
    
    try {
      console.log("🔄 Fetching all ULBs for mapping...");
      const response = await axios.get(`${API_BASE}/meta/ulbs`, { timeout: 15000 });
      let ulbs = [];
      
      if (response.data?.success && response.data.ulbs) {
        ulbs = response.data.ulbs;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        ulbs = response.data.data;
      } else if (Array.isArray(response.data)) {
        ulbs = response.data;
      }
      
      ulbs.forEach(ulb => {
        allUlbsMappingsCache.current.set(ulb.id, {
          id: ulb.id,
          name: ulb.ulb_name || ulb.name,
          district_id: ulb.district_id,
          division_id: ulb.division_id,
          ulb_type: ulb.ulb_type,
          ulb_class: ulb.ulb_class
        });
      });
      
      console.log(`✅ Cached ${allUlbsMappingsCache.current.size} ULBs with district/division mappings`);
      return allUlbsMappingsCache.current;
    } catch (err) {
      console.error("Error fetching ULB mappings:", err);
      return new Map();
    }
  }, [API_BASE]);

  // Function to print Summary Report section
  const handlePrintSummarySection = (title, data) => {
    if (!data || data.length === 0) {
      message.error(`No data found for ${title}`);
      return;
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      message.error('Please allow popups to print');
      return;
    }

    const printHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${title} Report</title>
          <meta charset="UTF-8">
          <style>
            body { font-family: 'Segoe UI', Arial, sans-serif; padding: 20px; margin: 0; color: #333; }
            .print-header { text-align: center; margin-bottom: 20px; padding-bottom: 10px; border-bottom: 2px solid #3b82f6; }
            .print-header h1 { margin: 0; color: #1e40af; font-size: 24px; }
            .print-header p { margin: 5px 0 0; color: #666; font-size: 12px; }
            .print-table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            .print-table th { background-color: #3b82f6; color: white; padding: 12px; text-align: left; border: 1px solid #ddd; }
            .print-table td { padding: 10px; border: 1px solid #ddd; }
            .print-table tr:nth-child(even) { background-color: #f9fafb; }
            .print-footer { margin-top: 20px; text-align: center; font-size: 12px; color: #666; padding-top: 10px; border-top: 1px solid #ddd; }
            @media print { body { margin: 0; padding: 15px; } }
          </style>
        </head>
        <body>
          <div class="print-header">
            <h1>${title}</h1>
            <p>Form: ${selectedFormForSummary?.form_name || '-'}</p>
            <p>Generated on: ${new Date().toLocaleString()}</p>
            <p>Total Records: ${data.length}</p>
          </div>
          <table class="print-table">
            <thead>
              <tr><th>ULB Name</th><th>District</th><th>Division</th><th>Status</th><th>Date</th></tr>
            </thead>
            <tbody>
              ${data.map(item => `
                <tr>
                  <td>${item.ulb_name || '-'}</td>
                  <td>${item.district_name || '-'}</td>
                  <td>${item.division_name || '-'}</td>
                  <td>${item.status || '-'}</td>
                  <td>${item.submitted_on ? new Date(item.submitted_on).toLocaleString() : '-'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="print-footer"><p>This is a computer generated report.</p></div>
        </body>
      </html>
    `;

    printWindow.document.write(printHtml);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 500);
  };

  // Function to fetch Summary Report data
  const fetchSummaryReportData = async (formRecord) => {
    try {
      setSelectedFormForSummary(formRecord);
      setSelectedFormId(formRecord.id);
      setSelectedForm(formRecord);
      setSummaryReportModalVisible(true);
      setLoadingSubmissions(true);
      setSummaryProgress(0);
      setSummaryLoadingStep('Initializing...');

      const updateProgress = (percent, step) => {
        setSummaryProgress(percent);
        setSummaryLoadingStep(step);
      };

      updateProgress(5, 'Preparing request...');

      const reportsParams = new URLSearchParams();
      reportsParams.append('limit', 100000);

      if (selectedDistrict && selectedDistrict !== 0 && selectedDistrict !== 'null') {
        reportsParams.append('district_id', selectedDistrict);
      }
      if (selectedDivision && selectedDivision !== 0 && selectedDivision !== 'null') {
        reportsParams.append('division_id', selectedDivision);
      }
      if (ulb && ulb.length > 0 && ulb[0] !== 0) {
        reportsParams.append('ulb_id', ulb.join(','));
      }
      if (ulbType && ulbType !== "all") {
        reportsParams.append('ulb_type', ulbType);
      }
      if (ulbClass && ulbClass !== "all") {
        reportsParams.append('ulb_class', ulbClass);
      }

      updateProgress(10, 'Loading reference data...');

      const districtMap = new Map();
      districtOptions.forEach(d => { if (d.id !== 0) districtMap.set(d.id, d.name); });
      const divisionMap = new Map();
      divisionOptions.forEach(d => { if (d.id !== 0) divisionMap.set(d.id, d.name); });
      const ulbMap = new Map();
      ulbOptions.forEach(u => { if (u.id !== 0) ulbMap.set(u.id, u.name); });

      updateProgress(15, 'Fetching submissions data...');

      const promises = [];

      const url = `${API_BASE}/reports/forms/${formRecord.id}/data?${reportsParams.toString()}`;
      promises.push(axios.get(url, { timeout: 115000 }));

      updateProgress(20, 'Fetching DAO approvals...');
      const daoParams = new URLSearchParams();
      daoParams.append('form_id', formRecord.id);
      if (selectedDistrict && selectedDistrict !== 0 && selectedDistrict !== 'null') daoParams.append('district_id', selectedDistrict);
      if (selectedDivision && selectedDivision !== 0 && selectedDivision !== 'null') daoParams.append('division_id', selectedDivision);
      if (ulb && ulb.length > 0 && ulb[0] !== 0) daoParams.append('ulb_id', ulb.join(','));
      const daoUrl = `${API_BASE}/workflow/dao-approved/list?${daoParams.toString()}`;
      promises.push(axios.get(daoUrl, { timeout: 115000 }));

      updateProgress(25, 'Fetching RDMA approvals...');
      const rdmaParams = new URLSearchParams();
      rdmaParams.append('form_id', formRecord.id);
      if (selectedDivision && selectedDivision !== 0 && selectedDivision !== 'null') rdmaParams.append('division_id', selectedDivision);
      const rdmaUrl = `${API_BASE}/workflow/rdma-approved/list?${rdmaParams.toString()}`;
      promises.push(axios.get(rdmaUrl, { timeout: 115000 }));

      updateProgress(30, 'Waiting responses...');
      const [response, daoResponse, rdmaResponse] = await Promise.all(promises);

      updateProgress(40, 'Processing submissions data...');

      let totalSubmissionsData = [];
      let filledSubmissionsData = [];
      let draftSubmissionsData = [];
      let dmaApprovedData = [];
      let slaBreachData = [];

      const formatItem = async (item, statusText) => {
        let districtName = item.district_name || item.district;
        if ((!districtName || districtName === 'Unknown') && item.district_id) {
          districtName = districtMap.get(item.district_id) || await getDistrictName(item.district_id);
        }
        let divisionName = item.division_name || item.division;
        if ((!divisionName || divisionName === 'Unknown') && item.division_id) {
          divisionName = divisionMap.get(item.division_id) || await getDivisionName(item.division_id);
        }
        let ulbName = item.ulb_name || item['ULB Name'] || item['Municipality Name'];
        if ((!ulbName || ulbName === 'Unknown') && item.ulb_id) {
          ulbName = ulbMap.get(item.ulb_id) || await getUlbName(item.ulb_id);
        }

        return {
          id: item._id || item.id,
          form_name: formRecord.form_name,
          ulb_name: ulbName || 'Unknown',
          district_name: districtName || 'Unknown',
          division_name: divisionName || 'Unknown',
          status: statusText,
          submitted_on: item._created_at || item.created_at || new Date().toISOString(),
        };
      };

      if (response.data?.success && response.data.data) {
        const submissions = response.data.data;
        for (const item of submissions) {
          totalSubmissionsData.push(await formatItem(item, item.status === 'draft' ? 'Draft' : (item.status === 'approved' ? 'Approved' : 'Submitted')));

          if (item.status !== 'draft') {
            filledSubmissionsData.push(await formatItem(item, 'Submitted'));
          }

          if (item.status === 'draft') {
            draftSubmissionsData.push(await formatItem(item, 'Pending'));
          }

          if (item.status === 'approved') {
            dmaApprovedData.push(await formatItem(item, 'DMA Approved'));
          }

          if (item.status === 'escalated') {
            slaBreachData.push(await formatItem(item, 'SLA Breach'));
          }
        }
      }

      updateProgress(70, 'Processing DAO approvals...');

      let daoApprovedData = [];
      if (daoResponse.data && daoResponse.data.success) {
        daoApprovedData = daoResponse.data.data.map(item => ({
          id: item.id || Math.random(),
          form_name: formRecord.form_name,
          ulb_name: item.ulb_name || item['ULB Name'] || 'Unknown',
          district_name: item.district_name || item.district || 'Unknown',
          division_name: item.division_name || item.division || 'Unknown',
          status: 'DAO Approved',
          submitted_on: item.created_at || new Date().toISOString(),
        }));
      }

      updateProgress(80, 'Processing RDMA approvals...');

      let rdmaApprovedData = [];
      if (rdmaResponse.data && rdmaResponse.data.success) {
        rdmaApprovedData = rdmaResponse.data.data.map(item => ({
          id: item.id || Math.random(),
          form_name: formRecord.form_name,
          ulb_name: item.ulb_name || item['ULB Name'] || 'Unknown',
          district_name: item.district_name || item.district || 'Unknown',
          division_name: item.division_name || item.division || 'Unknown',
          status: 'RDMA Approved',
          submitted_on: item.created_at || new Date().toISOString(),
        }));
      }

      updateProgress(90, 'Building report...');

      setSummaryReportData({
        totalSubmissions: totalSubmissionsData,
        filledSubmissions: filledSubmissionsData,
        draftSubmissions: draftSubmissionsData,
        daoApproved: daoApprovedData,
        rdmaApproved: rdmaApprovedData,
        dmaApproved: dmaApprovedData,
        slaBreach: slaBreachData
      });

      updateProgress(100, 'Complete!');

      setTimeout(() => {
        setLoadingSubmissions(false);
        setSummaryProgress(0);
        setSummaryLoadingStep('');
      }, 500);

    } catch (error) {
      console.error("Error fetching summary report:", error);
      message.error(`Error fetching summary report: ${error.message}`);
      setLoadingSubmissions(false);
      setSummaryReportModalVisible(false);
      setSummaryProgress(0);
      setSummaryLoadingStep('');
    }
  };

  // PRINT FUNCTION - prints data based on selected KPI
  const handlePrint = () => {
    let printData = [];
    let title = "Dashboard Report";
    let isSLA = false;

    if (selectedKpi === "filledSubmissions") {
      printData = allSubmissionsForPrint;
      title = "Filled Submissions";
    } else if (selectedKpi === "draftSubmissions") {
      printData = allSubmissionsForPrint;
      title = "Pending ULBs Submissions";
    } else if (selectedKpi === "dmaApproved") {
      printData = allSubmissionsForPrint;
      title = "DMA Approved Submissions";
    } else if (selectedKpi === "rdmaApproved") {
      printData = allSubmissionsForPrint;
      title = "RDMA Approved Submissions";
    } else if (selectedKpi === "totalSubmissions") {
      printData = allSubmissionsForPrint;
      title = "All Submissions";
    } else if (selectedKpi === "daoApproved") {
      printData = allSubmissionsForPrint;
      title = "DAO Approved Submissions";
    } else if (selectedKpi === "slaCompliance") {
      printData = allViolationsForPrint;
      title = "Escalated Submissions (SLA Breach)";
      isSLA = true;
    }

    if (printData.length === 0) {
      message.error('No data to print. Please load data first by clicking on the KPI.');
      return;
    }

    const originalTitle = document.title;
    document.title = title;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      message.error('Please allow popups to print');
      return;
    }

    const printHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${title}</title>
          <meta charset="UTF-8">
          <style>
            body {
              font-family: 'Segoe UI', Arial, sans-serif;
              padding: 20px;
              margin: 0;
              color: #333;
            }
            .print-header {
              text-align: center;
              margin-bottom: 20px;
              padding-bottom: 10px;
              border-bottom: 2px solid #3B82F6;
            }
            .print-header h1 {
              margin: 0;
              color: #1E40AF;
              font-size: 24px;
            }
            .print-header p {
              margin: 5px 0 0;
              color: #666;
              font-size: 12px;
            }
            .print-table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
            }
            .print-table th {
              background-color: #3B82F6;
              color: white;
              padding: 12px;
              text-align: left;
              border: 1px solid #ddd;
              font-size: 14px;
            }
            .print-table td {
              padding: 10px;
              border: 1px solid #ddd;
              font-size: 13px;
            }
            .print-table tr:nth-child(even) {
              background-color: #F9FAFB;
            }
            .print-footer {
              margin-top: 20px;
              text-align: center;
              font-size: 12px;
              color: #666;
              padding-top: 10px;
              border-top: 1px solid #ddd;
            }
            @media print {
              body { margin: 0; padding: 15px; }
              .print-table th { background-color: #3B82F6 !important; color: white !important; }
              .print-table td { border: 1px solid #ddd !important; }
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <h1>${title}</h1>
            <p>Generated on: ${new Date().toLocaleString()}</p>
            <p>Total Records: ${printData.length}</p>
          </div>
          <table class="print-table">
            <thead>
              <tr>
                <th>Form Name</th>
                <th>ULB Name</th>
                <th>District</th>
                <th>Division</th>
                <th>${isSLA ? 'Violated At' : 'Submitted On'}</th>
              </tr>
            </thead>
            <tbody>
              ${printData.map(item => `
                <tr>
                  <td>${item.form_name || '-'}</td>
                  <td>${item.ulb_name || '-'}</td>
                  <td>${item.district_name || '-'}</td>
                  <td>${item.division_name || '-'}</td>
                  <td>${isSLA ? (item.violated_at ? new Date(item.violated_at).toLocaleString() : '-') : (item.submitted_on ? new Date(item.submitted_on).toLocaleString() : '-')}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="print-footer">
            <p>This is a computer generated report.</p>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(printHtml);
    printWindow.document.close();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);

    document.title = originalTitle;
  };

  // Fetch user data from sessionStorage
  useEffect(() => {
    const fetchUserData = () => {
      try {
        const userStr = sessionStorage.getItem('user');
        const userRole = sessionStorage.getItem('userRole');
        const divisionId = sessionStorage.getItem('division_id');
        const districtId = sessionStorage.getItem('district_id');
        const ulbId = sessionStorage.getItem('ulb_id');

        let role = userRole;
        if (!role && userStr) {
          try {
            const user = JSON.parse(userStr);
            role = user.role_name;
          } catch (e) { }
        }

        const isDesk = role && (role.toLowerCase().includes('desk') || role.toLowerCase() === 'desk_user');

        console.log('👤 User role:', role, 'Is desk user:', isDesk);

        if (isDesk) {
          console.log('📌 Desk user: Will see ALL data (no filters applied)');
          setUserDivision(null);
          setUserDistrict(null);
          setUserUlb(null);
          setUserUlbId(null);
          setIsDeskUser(true);
          return;
        }

        setIsDeskUser(false);

        if (userStr) {
          try {
            const user = JSON.parse(userStr);
            setUserRole(user.role_name || userRole);

            const userDivId = divisionId || user.division_id;
            const userDistId = districtId || user.district_id;
            const userUlbId = ulbId || user.ulb_id;

            if (userDivId) {
              const divId = parseInt(userDivId);
              setUserDivision(divId);
              setSelectedDivision(divId);
              setDivision([divId]);
            }

            if (userDistId) {
              const distId = parseInt(userDistId);
              setUserDistrict(distId);
              setSelectedDistrict(distId);
              setDistrict([distId]);
            }

            if (userUlbId) {
              const ulbIdInt = parseInt(userUlbId);
              setUserUlb(ulbIdInt);
              setUserUlbId(ulbIdInt);
              setUlb([ulbIdInt]);
            }
          } catch (e) {
            console.error("Error parsing user object:", e);
          }
        } else {
          if (divisionId) {
            const divId = parseInt(divisionId);
            setUserDivision(divId);
            setSelectedDivision(divId);
            setDivision([divId]);
          }
          if (districtId) {
            const distId = parseInt(districtId);
            setUserDistrict(distId);
            setSelectedDistrict(distId);
            setDistrict([distId]);
          }
          if (ulbId) {
            const uId = parseInt(ulbId);
            setUserUlb(uId);
            setUserUlbId(uId);
            setUlb([uId]);
          }
          setUserRole(userRole);
        }
      } catch (error) {
        console.error("❌ Error fetching user data:", error);
      }
    };

    fetchUserData();
  }, []);

  // Fetch ULB name by ID
  const fetchUlbName = useCallback(async (ulbId) => {
    if (!ulbId) return;
    try {
      const response = await axios.get(`${API_BASE}/meta/ulb?id=${ulbId}`, {
        timeout: 10000
      });
      if (response.data.success && response.data.data) {
        setUserUlbName(response.data.data.ulb_name);
      }
    } catch (error) {
      console.error("Error fetching ULB name:", error);
    }
  }, [API_BASE]);

  // Fetch district and division details for selected ULB
  const fetchDistrictAndDivisionByUlb = useCallback(async (ulbId) => {
    if (!ulbId || ulbId === 0) {
      return { district: null, division: null };
    }
    try {
      const response = await axios.get(`${API_BASE}/meta/ulb?id=${ulbId}`, {
        timeout: 10000
      });
      if (response.data?.success && response.data.data) {
        const ulbData = response.data.data;
        const districtId = ulbData.district_id;
        const divisionId = ulbData.division_id;
        let districtName = null;
        let divisionName = null;

        if (districtId) {
          try {
            const districtRes = await axios.get(`${API_BASE}/meta/districts/${districtId}`, { timeout: 8000 });
            districtName = districtRes.data?.data?.name || districtRes.data?.name || null;
          } catch (err) {
            console.log("Could not fetch district name:", err.message);
          }
        }

        if (divisionId) {
          try {
            const divisionRes = await axios.get(`${API_BASE}/meta/divisions/${divisionId}`, { timeout: 8000 });
            divisionName = divisionRes.data?.data?.name || divisionRes.data?.name || null;
          } catch (err) {
            console.log("Could not fetch division name:", err.message);
          }
        }

        return {
          district: { id: districtId, name: districtName },
          division: { id: divisionId, name: divisionName }
        };
      }
      return { district: null, division: null };
    } catch (error) {
      console.error("Error fetching district/division for ULB:", error);
      return { district: null, division: null };
    }
  }, [API_BASE]);

  // Fetch ULB name if user is ULB
  useEffect(() => {
    if (userUlbId) {
      fetchUlbName(userUlbId);
    }
  }, [userUlbId, fetchUlbName]);

  // Parse ULB data
  const parseUlbData = useCallback((data) => {
    let ulbs = [];
    if (Array.isArray(data)) {
      ulbs = data.map(item => ({
        id: item.id,
        name: item.ulb_name || item.name || "Unknown ULB",
        ulb_type: item.ulb_type || "Municipal Council",
        ulb_class: item.class || item.ulb_class || "B",
        establishment_year: item.year_of_establishment || item.establishment_year || "",
        census_code: item.census_code || 0,
        district_id: item.district_id,
        division_id: item.division_id,
      }));
    } else if (data && typeof data === 'object') {
      if (data.success === true && Array.isArray(data.data)) {
        ulbs = data.data.map(item => ({
          id: item.id,
          name: item.ulb_name || item.name || "Unknown ULB",
          ulb_type: item.ulb_type || "Municipal Council",
          ulb_class: item.class || item.ulb_class || "B",
          establishment_year: item.year_of_establishment || item.establishment_year || "",
          census_code: item.census_code || 0,
          district_id: item.district_id,
          division_id: item.division_id
        }));
      } else if (Array.isArray(data.ulbs)) {
        ulbs = data.ulbs.map(item => ({
          id: item.id,
          name: item.ulb_name || item.name || "Unknown ULB",
          ulb_type: item.ulb_type || "Municipal Council",
          ulb_class: item.class || item.ulb_class || "B",
          establishment_year: item.year_of_establishment || item.establishment_year || "",
          census_code: item.census_code || 0,
          district_id: item.district_id,
          division_id: item.division_id
        }));
      }
    }
    return ulbs;
  }, []);

  const fetchAllForms = useCallback(async () => {
    try {
      setLoadingForms(true);
      let metadataMap = new Map();
      try {
        const metadataResponse = await axios.get(`${API_BASE}/forms/list`, { timeout: 10000 });
        if (metadataResponse.data && metadataResponse.data.data) {
          metadataResponse.data.data.forEach(form => {
            metadataMap.set(form.id, {
              form_name: form.form_name,
              fieldsCount: form.fieldsCount || 0,
            });
          });
        }
      } catch (metaError) {
        console.error('Failed to fetch form metadata:', metaError);
      }

      const response = await axios.get(`${API_BASE}/reports/forms`, {
        timeout: 30000,
        headers: { 'Cache-Control': 'no-cache' }
      });

      if (response.data.success) {
        const formsList = response.data.forms || [];
        const formsWithSubmissions = formsList.filter(form => (form.submissionCount || 0) > 0);
        const processedForms = formsWithSubmissions.map(form => {
          const metadata = metadataMap.get(form.id);
          let formName = metadata?.form_name || form.form_name || form.name || `Form ${form.id}`;
          return {
            id: form.id,
            form_name: formName,
            submissionCount: form.submissionCount || 0,
            fieldsCount: metadata?.fieldsCount || form.fieldsCount || 0,
            status: form.status || 'active',
            lastUpdated: form.lastUpdated || new Date().toISOString(),
          };
        });
        processedForms.sort((a, b) => a.form_name.localeCompare(b.form_name));
        setForms(processedForms);
        const totalForms = processedForms.length;
        const totalSubmissions = processedForms.reduce((sum, f) => sum + (f.submissionCount || 0), 0);
        const totalFields = processedForms.reduce((sum, f) => sum + (f.fieldsCount || 0), 0);
        setFormStats({
          totalForms,
          activeForms: totalForms,
          totalFields: totalFields,
          totalSubmissions,
          lastUpdated: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error("Error fetching forms:", error);
    } finally {
      setLoadingForms(false);
    }
  }, [API_BASE]);

  // Fetch all districts for a specific division
  const fetchDistrictsForDivision = useCallback(async (divisionId) => {
    if (!divisionId || divisionId === 0) {
      setAllDistrictsForChart([]);
      return [];
    }
    try {
      const res = await axios.get(`${API_BASE}/meta/districts`, {
        params: { division_id: divisionId },
        timeout: 10000
      });
      let districts = [];
      if (Array.isArray(res.data)) {
        districts = res.data.map(item => ({
          id: item.id || item.district_id,
          name: item.name || item.district_name
        }));
      } else if (res.data && typeof res.data === 'object' && res.data.success === true) {
        districts = (res.data.data || []).map(item => ({
          id: item.id || item.district_id,
          name: item.name || item.district_name
        }));
      }
      setAllDistrictsForChart(districts);
      return districts;
    } catch (err) {
      console.error("Error fetching districts for chart:", err);
      setAllDistrictsForChart([]);
      return [];
    }
  }, [API_BASE]);

  // Fetch all ULBs for a specific district
  const fetchUlbsForDistrict = useCallback(async (districtId) => {
    if (!districtId || districtId === 0) {
      setAllUlbsForChart([]);
      return [];
    }
    try {
      const res = await axios.get(`${API_BASE}/meta/ulbs`, {
        params: { district_id: districtId },
        timeout: 15000
      });
      let ulbs = [];
      const parsedData = parseUlbData(res.data);
      if (parsedData.length > 0) {
        ulbs = parsedData;
        if (ulbType && ulbType !== "all") {
          ulbs = ulbs.filter(ulb => ulb.ulb_type === ulbType);
        }
        if (ulbClass && ulbClass !== "all") {
          ulbs = ulbs.filter(ulb => ulb.ulb_class === ulbClass);
        }
      }
      setAllUlbsForChart(ulbs);
      return ulbs;
    } catch (err) {
      console.error("Error fetching ULBs for chart:", err);
      setAllUlbsForChart([]);
      return [];
    }
  }, [API_BASE, parseUlbData, ulbType, ulbClass]);

  // Determine X-axis type
  const determineXAxisType = useCallback(async () => {
    if (ulb.length > 0 && !(ulb.length === 1 && ulb[0] === 0)) {
      const selectedUlbs = ulbOptions.filter(opt => ulb.includes(opt.id) && opt.id !== 0);
      return {
        type: "ulb",
        data: selectedUlbs,
        label: language === "EN" ? "ULB" : "यूएलबी",
        isSingleULB: selectedUlbs.length === 1
      };
    }
    if (selectedDistrict) {
      const ulbs = await fetchUlbsForDistrict(selectedDistrict);
      return {
        type: "ulb",
        data: ulbs,
        label: language === "EN" ? "ULB" : "यूएलबी",
        isSingleULB: false
      };
    }
    if (selectedDivision) {
      const districts = await fetchDistrictsForDivision(selectedDivision);
      return {
        type: "district",
        data: districts,
        label: language === "EN" ? "District" : "जिल्हा",
        isSingleULB: false
      };
    }
    const divisionsToShow = divisionOptions.filter(div => div.id !== 0);
    return {
      type: "division",
      data: divisionsToShow,
      label: language === "EN" ? "Division" : "विभाग",
      isSingleULB: false
    };
  }, [ulb, ulbOptions, selectedDivision, selectedDistrict, divisionOptions, language, fetchDistrictsForDivision, fetchUlbsForDistrict]);

  // Update chart data based on filters and stats
  const updateChartData = useCallback(async (stats) => {
    if (!isMounted.current) return;
    const xAxis = await determineXAxisType();
    setXAxisConfig(xAxis);
    const slaBreachCount = stats.slaBreachCount || 0;
    const totalItems = xAxis.data.length || 1;
    const newSlaChartData = xAxis.data.map((item, index) => {
      let percentage = 0;
      if (slaBreachCount !== 0) {
        let breachFactor = 1;
        if (item.ulb_class === 'A') breachFactor = 0.3;
        else if (item.ulb_class === 'B') breachFactor = 0.6;
        else if (item.ulb_class === 'C') breachFactor = 0.9;
        else if (item.ulb_class === 'NP') breachFactor = 1.2;
        const baseBreachPercentage = (slaBreachCount / totalItems) * 15;
        percentage = Math.min(100, Math.max(0, baseBreachPercentage * breachFactor + (index % 3) * 2));
      }
      return {
        name: item.name,
        percentage: Math.round(percentage),
        type: xAxis.type === 'ulb' ? (item.ulb_type || 'ULB') : xAxis.type,
        class: item.ulb_class || '',
        slaStatus: percentage === 0 ? "Excellent" :
          percentage < 10 ? "Good" :
            percentage < 30 ? "Average" :
              percentage < 50 ? "Poor" : "Critical",
        isSingleULB: xAxis.isSingleULB,
        establishmentYear: item.establishment_year || '',
        censusCode: item.census_code || '',
        ulb_type: item.ulb_type || '',
        ulb_class: item.ulb_class || ''
      };
    });
    setSlaChartData(newSlaChartData);
    const newPieData = [
      { name: language === "EN" ? "Total Submissions" : "एकूण सबमिशन्स", value: stats.totalSubmissions, color: "#10b981" },
      { name: language === "EN" ? "Pending Submissions" : "प्रलंबित सबमिशन", value: stats.pendingSubmissions, color: "#dc2626" }
    ];
    setPieChartData(newPieData);
  }, [determineXAxisType, language]);

  const calculateSlaCategoryCounts = useCallback((ulbs, stats) => {
    if (!ulbs || ulbs.length === 0) {
      setSlaCategoryCounts({ excellent: 0, good: 0, average: 0, veryPoor: 0 });
      return;
    }
    const totalULBs = ulbs.length;
    const breachCount = stats.slaBreachCount || 0;
    if (breachCount === 0) {
      setSlaCategoryCounts({ excellent: totalULBs, good: 0, average: 0, veryPoor: 0 });
      return;
    }
    const complianceRate = Math.max(0, Math.min(100, totalULBs > 0 ? ((totalULBs - breachCount) / totalULBs) * 100 : 0));
    const counts = {
      excellent: Math.floor(totalULBs * (complianceRate / 100) * 0.8),
      good: Math.floor(totalULBs * (complianceRate / 100) * 0.15),
      average: Math.floor(totalULBs * (1 - complianceRate / 100) * 0.6),
      veryPoor: Math.floor(totalULBs * (1 - complianceRate / 100) * 0.4)
    };
    const total = Object.values(counts).reduce((a, b) => a + b, 0);
    if (total < totalULBs) counts.excellent += (totalULBs - total);
    setSlaCategoryCounts(counts);
  }, []);

  // Fetch DAO Approved count
  const fetchDaoApprovedCount = useCallback(async () => {
    try {
      const params = new URLSearchParams();

      if (selectedFormId) {
        params.append('form_id', selectedFormId);
      }

      if (selectedDistrict && selectedDistrict !== 0 && selectedDistrict !== 'null') {
        params.append('district_id', selectedDistrict);
      }
      if (selectedDivision && selectedDivision !== 0 && selectedDivision !== 'null') {
        params.append('division_id', selectedDivision);
      }
      if (ulb && ulb.length > 0 && ulb[0] !== 0) {
        params.append('ulb_id', ulb.filter(id => id !== 0).join(','));
      }

      const url = `${API_BASE}/workflow/dao-approved/count${params.toString() ? `?${params.toString()}` : ''}`;
      console.log("🔍 Fetching DAO count from:", url);

      const response = await axios.get(url, { timeout: 10000 });
      if (response.data && response.data.success) {
        console.log("✅ DAO Approved Count response:", response.data.count);
        return response.data.count || 0;
      }
      return 0;
    } catch (error) {
      console.error("Error fetching DAO approved count:", error);
      return 0;
    }
  }, [API_BASE, selectedFormId, selectedDistrict, selectedDivision, ulb]);

  // Fetch RDMA Approved count
  const fetchRdmaApprovedCount = useCallback(async () => {
    try {
      const params = new URLSearchParams();

      if (selectedFormId) {
        params.append('form_id', selectedFormId);
      }

      if (selectedDistrict && selectedDistrict !== 0 && selectedDistrict !== 'null') {
        params.append('district_id', selectedDistrict);
      }
      if (selectedDivision && selectedDivision !== 0 && selectedDivision !== 'null') {
        params.append('division_id', selectedDivision);
      }
      if (ulb && ulb.length > 0 && ulb[0] !== 0) {
        params.append('ulb_id', ulb.filter(id => id !== 0).join(','));
      }

      const url = `${API_BASE}/workflow/rdma-approved/count${params.toString() ? `?${params.toString()}` : ''}`;
      console.log("🔍 Fetching RDMA count from:", url);

      const response = await axios.get(url, { timeout: 10000 });
      if (response.data && response.data.success) {
        console.log("✅ RDMA Approved Count response:", response.data.count);
        return response.data.count || 0;
      }
      return 0;
    } catch (error) {
      console.error("Error fetching RDMA approved count:", error);
      return 0;
    }
  }, [API_BASE, selectedFormId, selectedDistrict, selectedDivision, ulb]);

  // Fetch dashboard stats
  const fetchDashboardStats = useCallback(async () => {
    if (isFetching || !isMounted.current) return;
    try {
      setIsFetching(true);
      setLoadingStats(true);
      let filledCount = 0, draftCount = 0, dmaApprovedCount = 0, totalSubmissionsCount = 0, slaBreachCount = 0;

      let daoApprovedCount = await fetchDaoApprovedCount();
      let rdmaApprovedCount = await fetchRdmaApprovedCount();

      const reportsParams = new URLSearchParams();
      reportsParams.append('limit', 10000);
      if (selectedDistrict && selectedDistrict !== 0) reportsParams.append('district_id', selectedDistrict);
      if (selectedDivision && selectedDivision !== 0) reportsParams.append('division_id', selectedDivision);
      if (ulb && ulb.length > 0 && ulb[0] !== 0) reportsParams.append('ulb_id', ulb.join(','));

      if (selectedFormId) {
        try {
          const url = `${API_BASE}/reports/forms/${selectedFormId}/data?${reportsParams.toString()}`;
          const response = await axios.get(url, { timeout: 10000 });
          if (response.data?.success && response.data.data) {
            const submissions = response.data.data;
            filledCount = submissions.filter(s => s.status !== 'draft').length;
            draftCount = submissions.filter(s => s.status === 'draft').length;
            dmaApprovedCount = submissions.filter(s => s.status === 'approved').length;
            totalSubmissionsCount = submissions.length;
            slaBreachCount = submissions.filter(s => s.status === 'escalated').length;
          }
        } catch (err) {
          console.error("Error fetching submissions for selected form:", err);
        }
      } else {
        for (const form of forms) {
          try {
            const url = `${API_BASE}/reports/forms/${form.id}/data?${reportsParams.toString()}`;
            const response = await axios.get(url, { timeout: 10000 });
            if (response.data?.success && response.data.data) {
              const submissions = response.data.data;
              filledCount += submissions.filter(s => s.status !== 'draft').length;
              draftCount += submissions.filter(s => s.status === 'draft').length;
              dmaApprovedCount += submissions.filter(s => s.status === 'approved').length;
              totalSubmissionsCount += submissions.length;
              slaBreachCount += submissions.filter(s => s.status === 'escalated').length;
            }
          } catch (err) {
            console.error(`Error fetching form ${form.id}:`, err);
          }
        }
      }

      const totalFormsCount = formStats.totalForms || forms.length || 0;
      const totalFieldsCount = formStats.totalFields || 0;
      const selectedULBsCount = ulb.length > 0 && ulb[0] !== 0 ? ulb.length : 1;
      const reports = totalFormsCount * selectedULBsCount;

      console.log("📊 Dashboard Stats Updated:", {
        daoApproved: daoApprovedCount,
        rdmaApproved: rdmaApprovedCount,
        totalSubmissions: totalSubmissionsCount,
        filledSubmissions: filledCount
      });

      setDashboardStats({
        totalForms: totalFormsCount,
        totalFields: totalFieldsCount,
        totalSubmissions: totalSubmissionsCount,
        filledSubmissions: filledCount,
        draftSubmissions: draftCount,
        dmaApproved: dmaApprovedCount,
        daoApproved: daoApprovedCount,
        rdmaApproved: rdmaApprovedCount,
        reports: reports,
        submissionsReceived: totalSubmissionsCount,
        slaCompliance: totalSubmissionsCount > 0 ? `${Math.round((slaBreachCount / totalSubmissionsCount) * 100)}%` : "0%",
        selectedULBsCount: selectedULBsCount,
        activeForms: totalFormsCount,
        slaBreachCount: slaBreachCount
      });
    } catch (error) {
      console.error("Error in dashboard stats:", error);
    } finally {
      if (isMounted.current) {
        setIsFetching(false);
        setLoadingStats(false);
      }
    }
  }, [isFetching, ulb, formStats, forms, selectedFormId, selectedDistrict, selectedDivision, API_BASE, fetchDaoApprovedCount, fetchRdmaApprovedCount]);

  // fetchSubmissionsData - UPDATED with proper district/division mapping
  const fetchSubmissionsData = useCallback(async (page = 1, statusFilter = null, forceRefresh = false) => {
  try {
    const cacheKey = JSON.stringify({
      selectedDivision,
      selectedDistrict,
      ulb: [...ulb].sort(),
      selectedFormId,
      ulbType,
      ulbClass,
      statusFilter
    });

    if (!forceRefresh && cachedSubmissions && cachedSubmissionsKey === cacheKey) {
      console.log("📦 Using cached submissions data");
      const allSubmissions = cachedSubmissions;
      const total = allSubmissions.length;
      const startIndex = (page - 1) * submissionsPagination.pageSize;
      const paginatedSubmissions = allSubmissions.slice(startIndex, startIndex + submissionsPagination.pageSize);

      setSubmissionsData(paginatedSubmissions);
      setAllSubmissionsForPrint(allSubmissions);
      setSubmissionsPagination(prev => ({ ...prev, current: page, total: total }));
      return;
    }

    setLoadingSubmissions(true);

    // First, fetch all ULB mappings
    const ulbMappings = await fetchAllUlbsWithMappings();
    console.log("📋 ULB Mappings size:", ulbMappings.size);
    
    // Also fetch district mappings
    const districtMap = new Map();
    districtOptions.forEach(d => { if (d.id !== 0) districtMap.set(d.id, d.name); });
    
    // Fetch division mappings  
    const divisionMap = new Map();
    divisionOptions.forEach(d => { if (d.id !== 0) divisionMap.set(d.id, d.name); });

    const reportsParams = new URLSearchParams();
    reportsParams.append('limit', 100000);
    if (selectedDistrict && selectedDistrict !== 0) reportsParams.append('district_id', selectedDistrict);
    if (selectedDivision && selectedDivision !== 0) reportsParams.append('division_id', selectedDivision);
    if (ulb && ulb.length > 0 && ulb[0] !== 0) reportsParams.append('ulb_id', ulb.join(','));
    if (ulbType && ulbType !== "all") reportsParams.append('ulb_type', ulbType);
    if (ulbClass && ulbClass !== "all") reportsParams.append('ulb_class', ulbClass);

    let allSubmissions = [];

    const getDistrictNameFromId = async (districtId) => {
      if (!districtId || districtId === 0) return 'Unknown';
      if (districtMap.has(districtId)) return districtMap.get(districtId);
      
      try {
        const response = await axios.get(`${API_BASE}/meta/districts/${districtId}`, { timeout: 5000 });
        if (response.data?.success && response.data.data?.name) {
          const name = response.data.data.name;
          districtMap.set(districtId, name);
          return name;
        }
      } catch (err) {
        console.log(`Could not fetch district ${districtId}:`, err.message);
      }
      return `District ${districtId}`;
    };

    const getDivisionNameFromId = async (divisionId) => {
      if (!divisionId || divisionId === 0) return 'Unknown';
      if (divisionMap.has(divisionId)) return divisionMap.get(divisionId);
      
      try {
        const response = await axios.get(`${API_BASE}/meta/divisions/${divisionId}`, { timeout: 5000 });
        if (response.data?.success && response.data.data?.name) {
          const name = response.data.data.name;
          divisionMap.set(divisionId, name);
          return name;
        }
      } catch (err) {
        console.log(`Could not fetch division ${divisionId}:`, err.message);
      }
      return `Division ${divisionId}`;
    };

    const processSubmission = async (item, formName, formId) => {
      // Try multiple possible field names for ULB ID
      let ulbId = item.ulb_id || item.ulbId || item.municipality_id || item.city_id;
      let ulbName = item.ulb_name || item.ulbName || item.municipality_name || item.city_name || item['ULB Name'] || item['Municipality Name'];
      let districtName = item.district_name || item.district;
      let divisionName = item.division_name || item.division;
      
      // If we have ulb_id but no district/division, get from ULB mapping
      if (ulbId && (!districtName || districtName === 'Unknown' || !divisionName || divisionName === 'Unknown')) {
        const ulbInfo = ulbMappings.get(parseInt(ulbId));
        console.log(`Looking up ULB ${ulbId}:`, ulbInfo);
        
        if (ulbInfo) {
          // Set ULB name if not already set
          if (!ulbName || ulbName === 'Unknown') {
            ulbName = ulbInfo.name;
          }
          
          // Get district from ULB mapping
          if (ulbInfo.district_id) {
            districtName = await getDistrictNameFromId(ulbInfo.district_id);
          }
          
          // Get division from ULB mapping
          if (ulbInfo.division_id) {
            divisionName = await getDivisionNameFromId(ulbInfo.division_id);
          }
        }
      }
      
      // If we still don't have district/division, try to get from item directly
      if ((!districtName || districtName === 'Unknown') && item.district_id) {
        districtName = await getDistrictNameFromId(item.district_id);
      }
      
      if ((!divisionName || divisionName === 'Unknown') && item.division_id) {
        divisionName = await getDivisionNameFromId(item.division_id);
      }

      console.log(`Processed submission: ULB=${ulbName}, District=${districtName}, Division=${divisionName}`);

      return {
        id: item._id || item.id,
        submission_id: item._submission_id || item.submission_id || item.id,
        form_name: formName,
        form_id: formId,
        ulb_name: ulbName || 'Unknown',
        ulb_id: ulbId,
        district_name: districtName || 'Unknown',
        district_id: item.district_id,
        division_name: divisionName || 'Unknown',
        division_id: item.division_id,
        submitted_by: item._user_id || item.user_id || item.submitted_by || 'System',
        submitted_on: item._created_at || item.created_at || item.submitted_on || new Date().toISOString(),
        status: item.status || 'filled'
      };
    };

    if (selectedFormId) {
      const url = `${API_BASE}/reports/forms/${selectedFormId}/data?${reportsParams.toString()}`;
      console.log("Fetching submissions from:", url);
      const response = await axios.get(url, { timeout: 1150000 });
      console.log("Submissions response:", response.data);
      
      if (response.data?.success && response.data.data) {
        let submissions = response.data.data;
        console.log(`Found ${submissions.length} total submissions`);
        
        if (statusFilter === 'filled') {
          submissions = submissions.filter(s => s.status !== 'draft');
          console.log(`After filled filter: ${submissions.length}`);
        } else if (statusFilter === 'draft') {
          submissions = submissions.filter(s => s.status === 'draft');
          console.log(`After draft filter: ${submissions.length}`);
        } else if (statusFilter === 'approved') {
          submissions = submissions.filter(s => s.status === 'approved');
          console.log(`After approved filter: ${submissions.length}`);
        }

        // Log first submission to see structure
        if (submissions.length > 0) {
          console.log("Sample submission structure:", JSON.stringify(submissions[0], null, 2));
        }

        for (const item of submissions) {
          const processed = await processSubmission(item, selectedForm?.form_name || `Form ${selectedFormId}`, selectedFormId);
          allSubmissions.push(processed);
        }
      } else {
        console.log("No data in response or success false");
      }
    } else {
      // No form selected - fetch from ALL forms
      for (const form of forms) {
        try {
          const url = `${API_BASE}/reports/forms/${form.id}/data?${reportsParams.toString()}`;
          console.log(`Fetching form ${form.id} from:`, url);
          const response = await axios.get(url, { timeout: 1150000 });
          
          if (response.data?.success && response.data.data) {
            let submissions = response.data.data;
            console.log(`Form ${form.id}: found ${submissions.length} submissions`);
            
            if (statusFilter === 'filled') {
              submissions = submissions.filter(s => s.status !== 'draft');
            } else if (statusFilter === 'draft') {
              submissions = submissions.filter(s => s.status === 'draft');
            } else if (statusFilter === 'approved') {
              submissions = submissions.filter(s => s.status === 'approved');
            }

            for (const item of submissions) {
              const processed = await processSubmission(item, form.form_name, form.id);
              allSubmissions.push(processed);
            }
          }
        } catch (err) {
          console.log(`Error fetching form ${form.id}:`, err);
        }
      }
      allSubmissions.sort((a, b) => new Date(b.submitted_on) - new Date(a.submitted_on));
    }

    console.log(`Total processed submissions: ${allSubmissions.length}`);
    console.log("Sample of first submission:", allSubmissions[0]);

    setCachedSubmissions(allSubmissions);
    setCachedSubmissionsKey(cacheKey);
    setAllSubmissionsForPrint(allSubmissions);

    const total = allSubmissions.length;
    const startIndex = (page - 1) * submissionsPagination.pageSize;
    const paginatedSubmissions = allSubmissions.slice(startIndex, startIndex + submissionsPagination.pageSize);
    setSubmissionsData(paginatedSubmissions);
    setSubmissionsPagination(prev => ({ ...prev, current: page, total: total }));

  } catch (error) {
    console.error("Error fetching submissions:", error);
    message.error(`Error fetching submissions: ${error.message}`);
    setSubmissionsData([]);
  } finally {
    setLoadingSubmissions(false);
  }
}, [API_BASE, selectedDivision, selectedDistrict, ulb, selectedFormId, selectedForm, submissionsPagination.pageSize, forms, ulbType, ulbClass, districtOptions, divisionOptions, cachedSubmissions, cachedSubmissionsKey, fetchAllUlbsWithMappings]);
  // fetchSLAViolations - WITH CACHING
  const fetchSLAViolations = useCallback(async (page = 1, forceRefresh = false) => {
    try {
      const cacheKey = JSON.stringify({
        selectedDivision,
        selectedDistrict,
        ulb: [...ulb].sort(),
        selectedFormId,
        ulbType,
        ulbClass
      });

      if (!forceRefresh && cachedViolations && cachedViolationsKey === cacheKey) {
        console.log("📦 Using cached SLA violations data");
        const allViolations = cachedViolations;
        const total = allViolations.length;
        const startIndex = (page - 1) * slaViolationsPagination.pageSize;
        const paginatedSubmissions = allViolations.slice(startIndex, startIndex + slaViolationsPagination.pageSize);

        setSlaViolations(paginatedSubmissions);
        setAllViolationsForPrint(allViolations);
        setSlaViolationsPagination(prev => ({ ...prev, current: page, total: total }));
        setSlaSummary({ total_violations: total, open_violations: total, resolved_violations: 0, total_workflows: total, avg_resolution_hours: 0, compliance_by_form: [] });
        return;
      }

      setLoadingSlaViolations(true);
      let escalatedSubmissions = [];

      if (selectedFormId) {
        const response = await axios.get(`${API_BASE}/reports/forms/${selectedFormId}/data`, {
          timeout: 1150000,
          params: { limit: 100000 }
        });
        if (response.data?.success && response.data.data) {
          const submissions = response.data.data;
          for (const s of submissions) {
            if (s.status === 'escalated') {
              let ulbName = s.ulb_name || s['ULB Name'] || s['Municipality Name'];
              if ((!ulbName || ulbName === 'Unknown') && s.ulb_id) ulbName = await getUlbName(s.ulb_id);
              let districtName = s.district_name || s.district;
              if ((!districtName || districtName === 'Unknown' || districtName.startsWith('District')) && s.district_id) {
                districtName = await getDistrictName(s.district_id);
              }
              let divisionName = s.division_name || s.division;
              if ((!divisionName || divisionName === 'Unknown' || divisionName.startsWith('Division')) && s.division_id) {
                divisionName = await getDivisionName(s.division_id);
              }
              escalatedSubmissions.push({
                id: s._id || s.id,
                submission_id: s._submission_id || s.submission_id,
                form_name: selectedForm?.form_name || `Form ${selectedFormId}`,
                ulb_name: ulbName || 'Unknown',
                district_name: districtName || 'Unknown',
                division_name: divisionName || 'Unknown',
                violated_at: s._created_at || s.created_at || s.updated_at || new Date().toISOString(),
                breach_status: 'Active Breach',
                status: s.status
              });
            }
          }
        }
      } else {
        for (const form of forms) {
          try {
            const response = await axios.get(`${API_BASE}/reports/forms/${form.id}/data`, {
              timeout: 1150000,
              params: { limit: 100000 }
            });
            if (response.data?.success && response.data.data) {
              const submissions = response.data.data;
              for (const s of submissions) {
                if (s.status === 'escalated') {
                  let ulbName = s.ulb_name || s['ULB Name'] || s['Municipality Name'];
                  if ((!ulbName || ulbName === 'Unknown') && s.ulb_id) ulbName = await getUlbName(s.ulb_id);
                  let districtName = s.district_name || s.district;
                  if ((!districtName || districtName === 'Unknown' || districtName.startsWith('District')) && s.district_id) {
                    districtName = await getDistrictName(s.district_id);
                  }
                  let divisionName = s.division_name || s.division;
                  if ((!divisionName || divisionName === 'Unknown' || divisionName.startsWith('Division')) && s.division_id) {
                    divisionName = await getDivisionName(s.division_id);
                  }
                  escalatedSubmissions.push({
                    id: s._id || s.id,
                    submission_id: s._submission_id || s.submission_id,
                    form_name: form.form_name,
                    ulb_name: ulbName || 'Unknown',
                    district_name: districtName || 'Unknown',
                    division_name: divisionName || 'Unknown',
                    violated_at: s._created_at || s.created_at || s.updated_at || new Date().toISOString(),
                    breach_status: 'Active Breach',
                    status: s.status
                  });
                }
              }
            }
          } catch (err) { }
        }
      }

      escalatedSubmissions.sort((a, b) => new Date(b.violated_at) - new Date(a.violated_at));

      setCachedViolations(escalatedSubmissions);
      setCachedViolationsKey(cacheKey);
      setAllViolationsForPrint(escalatedSubmissions);

      const total = escalatedSubmissions.length;
      const startIndex = (page - 1) * slaViolationsPagination.pageSize;
      const paginatedSubmissions = escalatedSubmissions.slice(startIndex, startIndex + slaViolationsPagination.pageSize);
      setSlaViolations(paginatedSubmissions);
      setSlaViolationsPagination(prev => ({ ...prev, current: page, total: total }));
      setSlaSummary({ total_violations: total, open_violations: total, resolved_violations: 0, total_workflows: total, avg_resolution_hours: 0, compliance_by_form: [] });

    } catch (error) {
      console.error("Error fetching SLA violations:", error);
      message.error(`Error fetching escalated submissions: ${error.message}`);
      setSlaViolations([]);
    } finally {
      setLoadingSlaViolations(false);
    }
  }, [API_BASE, selectedFormId, selectedForm, forms, slaViolationsPagination.pageSize, getUlbName, getDistrictName, getDivisionName, selectedDistrict, selectedDivision, ulb, ulbType, ulbClass, cachedViolations, cachedViolationsKey]);

  // Fetch divisions
  const fetchDivisions = useCallback(async () => {
    if (!isMounted.current) return;
    try {
      setLoadingDivision(true);
      const res = await axios.get(`${API_BASE}/meta/divisions`, {
        params: { state_id: MAHARASHTRA_STATE_ID },
        timeout: 10000
      });
      let formattedDivisions = [];
      if (Array.isArray(res.data)) {
        formattedDivisions = res.data.map(item => ({ id: item.id || item.division_id, name: item.name || item.division_name }));
      } else if (res.data && typeof res.data === 'object' && res.data.success === true) {
        formattedDivisions = (res.data.data || []).map(item => ({ id: item.id || item.division_id, name: item.name || item.division_name }));
      }
      if (isMounted.current) {
        setDivisionOptions([{ id: 0, name: "All Divisions" }, ...formattedDivisions]);
        if (userDivision) {
          const userDiv = formattedDivisions.find(d => d.id === userDivision);
          if (userDiv) setUserDivisionName(userDiv.name);
        }
      }
    } catch (err) {
      console.error("Divisions ERROR:", err);
      if (isMounted.current) setDivisionOptions([{ id: 0, name: "All Divisions" }]);
    } finally {
      if (isMounted.current) setLoadingDivision(false);
    }
  }, [API_BASE, userDivision]);

  // Fetch districts
  const fetchDistricts = useCallback(async (divisionId) => {
    if (!divisionId || divisionId === 0 || !isMounted.current) {
      setDistrictOptions([{ id: 0, name: "All Districts" }]);
      setSelectedUlbDetails([]);
      setUlb([]);
      return;
    }
    try {
      setLoadingDistrict(true);
      const res = await axios.get(`${API_BASE}/meta/districts`, {
        params: { division_id: divisionId },
        timeout: 10000
      });
      let formattedDistricts = [];
      if (Array.isArray(res.data)) {
        formattedDistricts = res.data.map(item => ({ id: item.id || item.district_id, name: item.name || item.district_name }));
      } else if (res.data && typeof res.data === 'object' && res.data.success === true) {
        formattedDistricts = (res.data.data || []).map(item => ({ id: item.id || item.district_id, name: item.name || item.district_name }));
      }
      if (isMounted.current) {
        setDistrictOptions([{ id: 0, name: "All Districts" }, ...formattedDistricts]);
        if (userDistrict) {
          const userDist = formattedDistricts.find(d => d.id === userDistrict);
          if (userDist) setUserDistrictName(userDist.name);
        }
      }
    } catch (err) {
      console.error("Districts ERROR:", err);
      if (isMounted.current) setDistrictOptions([{ id: 0, name: "All Districts" }]);
    } finally {
      if (isMounted.current) setLoadingDistrict(false);
    }
  }, [API_BASE, userDistrict]);

  // Fetch ULBs - UPDATED to include district_id and division_id
  const fetchUlbs = useCallback(async (districtId) => {
    if (!districtId || districtId === 0 || !isMounted.current) {
      setUlbOptions([{ id: 0, name: "All ULBs" }]);
      setSelectedUlbDetails([]);
      setUlb([]);
      setUlbError(null);
      return;
    }
    try {
      setLoadingUlb(true);
      setUlbError(null);
      const res = await axios.get(`${API_BASE}/meta/ulbs`, {
        params: { district_id: districtId },
        timeout: 15000
      });
      let formattedUlbs = [];
      const parsedData = parseUlbData(res.data);
      if (parsedData.length > 0) {
        formattedUlbs = parsedData;
        if (ulbType && ulbType !== "all") formattedUlbs = formattedUlbs.filter(ulb => ulb.ulb_type === ulbType);
        if (ulbClass && ulbClass !== "all") formattedUlbs = formattedUlbs.filter(ulb => ulb.ulb_class === ulbClass);
      }
      if (isMounted.current) {
        if (formattedUlbs.length === 0) {
          setUlbError(t.noULBsFound);
          setUlbOptions([{ id: 0, name: "All ULBs" }]);
        } else {
          // Create options with district_id and division_id
          const optionsWithLocation = formattedUlbs.map(ulb => ({
            id: ulb.id,
            name: ulb.name,
            district_id: ulb.district_id,
            division_id: ulb.division_id,
            ulb_type: ulb.ulb_type,
            ulb_class: ulb.ulb_class
          }));
          setUlbOptions([{ id: 0, name: "All ULBs" }, ...optionsWithLocation]);
        }
      }
    } catch (err) {
      console.error("ULBs ERROR:", err);
      if (isMounted.current) {
        setUlbError(`${t.failedToLoadULBs || 'Failed to load ULBs'}: ${err.message}`);
        setUlbOptions([{ id: 0, name: "All ULBs" }]);
      }
    } finally {
      if (isMounted.current) setLoadingUlb(false);
    }
  }, [API_BASE, t, parseUlbData, ulbType, ulbClass]);

  // Auto-populate district and division when ULB is selected
  useEffect(() => {
    const autoPopulateFromUlb = async () => {
      if (ulb.length === 1 && ulb[0] !== 0) {
        const { district, division } = await fetchDistrictAndDivisionByUlb(ulb[0]);
        if (district && district.id && !selectedDistrict) {
          setSelectedDistrict(district.id);
          setDistrict([district.id]);
          fetchUlbs(district.id);
          fetchUlbsForDistrict(district.id);
        }
        if (division && division.id && !selectedDivision) {
          setSelectedDivision(division.id);
          setDivision([division.id]);
          fetchDistricts(division.id);
          fetchDistrictsForDivision(division.id);
        }
      }
    };
    autoPopulateFromUlb();
  }, [ulb, selectedDistrict, selectedDivision, fetchDistrictAndDivisionByUlb, fetchUlbs, fetchUlbsForDistrict, fetchDistricts, fetchDistrictsForDivision]);

  // Handle ULB selection
  const handleUlbSelect = (value) => {
    setUlb(value);
    if (value.length > 0) {
    } else {
      setSelectedUlbDetails([]);
    }
  };

  const handleSelectChange = (setter, value, ref) => {
    setter(value);
    setTimeout(() => { if (ref.current) ref.current.blur(); }, 100);
  };

  const handleOptionClick = (ref) => {
    setTimeout(() => { if (ref.current) ref.current.blur(); }, 100);
  };

  const handleFormSelect = (formId) => {
    if (formSelectRef.current) formSelectRef.current.blur();
    setTimeout(() => {
      if (!formId || formId === 0) {
        setSelectedForm(null);
        setSelectedFormId(null);
        fetchDashboardStats();
        return;
      }
      const selected = forms.find(form => form.id === formId);
      if (selected) {
        setSelectedForm(selected);
        setSelectedFormId(formId);
        fetchDashboardStats();
      }
    }, 100);
  };

  const handleReset = () => {
    setUlbType("all");
    setUlbClass("all");
    if (isDeskUser) {
      setDivision([]);
      setSelectedDivision(null);
      setDistrict([]);
      setSelectedDistrict(null);
      setUlb([]);
    } else {
      if (userDivision) { setDivision([userDivision]); setSelectedDivision(userDivision); }
      else { setDivision([]); setSelectedDivision(null); }
      if (userDistrict) { setDistrict([userDistrict]); setSelectedDistrict(userDistrict); }
      else { setDistrict([]); setSelectedDistrict(null); }
      if (isUlbUser && userUlbId) setUlb([userUlbId]);
      else setUlb([]);
    }
    setUlbError(null);
    setSelectedUlbDetails([]);
    setSelectedForm(null);
    setSelectedFormId(null);
    setSelectedKpi("slaCompliance");
    setAllDistrictsForChart([]);
    setAllUlbsForChart([]);
  };

  // Handle KPI Click
  const handleKpiClick = async (kpiId) => {
    setSelectedKpi(kpiId);

    if (kpiId === "totalForms") {
      setFormsModalVisible(true);
    }
    else if (kpiId === "totalSubmissions") {
      fetchSubmissionsData(1, null);
      setSubmissionsModalVisible(true);
    }
    else if (kpiId === "filledSubmissions") {
      fetchSubmissionsData(1, 'filled');
      setSubmissionsModalVisible(true);
    }
    else if (kpiId === "draftSubmissions") {
      fetchSubmissionsData(1, 'draft');
      setSubmissionsModalVisible(true);
    }
    else if (kpiId === "daoApproved") {
      setLoadingSubmissions(true);
      try {
        let formIdsToUse = [];
        if (selectedFormId) {
          formIdsToUse = [selectedFormId];
        } else {
          formIdsToUse = forms.map(form => form.id);
        }

        if (formIdsToUse.length === 0) {
          message.info("No forms available");
          setLoadingSubmissions(false);
          return;
        }

        let allSubmissions = [];

        for (const formId of formIdsToUse) {
          const params = new URLSearchParams();
          params.append('form_id', formId);
          if (selectedDistrict && selectedDistrict !== 0) params.append('district_id', selectedDistrict);
          if (selectedDivision && selectedDivision !== 0) params.append('division_id', selectedDivision);
          if (ulb && ulb.length > 0 && ulb[0] !== 0) params.append('ulb_id', ulb.join(','));
          const url = `${API_BASE}/workflow/dao-approved/list?${params.toString()}`;
          const response = await axios.get(url, { timeout: 1150000 });

          if (response.data && response.data.success && response.data.data) {
            const form = forms.find(f => f.id === formId);
            const submissionsWithFormName = response.data.data.map(sub => ({
              ...sub,
              form_name: form?.form_name || `Form ${formId}`
            }));
            allSubmissions = [...allSubmissions, ...submissionsWithFormName];
          }
        }

        setAllSubmissionsForPrint(allSubmissions);
        setSubmissionsData(allSubmissions);
        setSubmissionsPagination(prev => ({ ...prev, current: 1, total: allSubmissions.length }));
        message.success(`Found ${allSubmissions.length} DAO approved submissions from ${formIdsToUse.length} form(s)`);

      } catch (error) {
        console.error("Error fetching DAO submissions:", error);
        message.error(`Error: ${error.message}`);
        setSubmissionsData([]);
      } finally {
        setLoadingSubmissions(false);
      }
      setSubmissionsModalVisible(true);
    }
    else if (kpiId === "rdmaApproved") {
      setLoadingSubmissions(true);
      try {
        let formIdsToUse = [];
        if (selectedFormId) {
          formIdsToUse = [selectedFormId];
        } else {
          formIdsToUse = forms.map(form => form.id);
        }

        if (formIdsToUse.length === 0) {
          message.info("No forms available");
          setLoadingSubmissions(false);
          return;
        }

        let allSubmissions = [];

        for (const formId of formIdsToUse) {
          const params = new URLSearchParams();
          params.append('form_id', formId);
          if (userDivision && userDivision !== 'null') params.append('division_id', userDivision);
          else if (selectedDivision && selectedDivision !== 0) params.append('division_id', selectedDivision);
          const url = `${API_BASE}/workflow/rdma-approved/list?${params.toString()}`;
          const response = await axios.get(url, { timeout: 1150000 });

          if (response.data && response.data.success && response.data.data) {
            const form = forms.find(f => f.id === formId);
            const submissionsWithFormName = response.data.data.map(sub => ({
              ...sub,
              form_name: form?.form_name || `Form ${formId}`
            }));
            allSubmissions = [...allSubmissions, ...submissionsWithFormName];
          }
        }

        setAllSubmissionsForPrint(allSubmissions);
        setSubmissionsData(allSubmissions);
        setSubmissionsPagination(prev => ({ ...prev, current: 1, total: allSubmissions.length }));
        message.success(`Found ${allSubmissions.length} RDMA approved submissions from ${formIdsToUse.length} form(s)`);

      } catch (error) {
        console.error("Error fetching RDMA submissions:", error);
        message.error(`Error: ${error.message}`);
        setSubmissionsData([]);
      } finally {
        setLoadingSubmissions(false);
      }
      setSubmissionsModalVisible(true);
    }
    else if (kpiId === "dmaApproved") {
      fetchSubmissionsData(1, 'approved');
      setSubmissionsModalVisible(true);
    }
    else if (kpiId === "slaCompliance") {
      fetchSLAViolations(1);
      setSlaViolationsModalVisible(true);
    }
  };

  const handleSubmissionsTableChange = (pagination) => {
    setSubmissionsPagination({
      current: pagination.current,
      pageSize: pagination.pageSize,
      total: cachedSubmissions?.length || 0
    });

    if (cachedSubmissions) {
      const startIndex = (pagination.current - 1) * pagination.pageSize;
      const paginatedData = cachedSubmissions.slice(startIndex, startIndex + pagination.pageSize);
      setSubmissionsData(paginatedData);
    } else {
      fetchSubmissionsData(pagination.current);
    }
  };

  const handleSlaViolationsTableChange = (pagination) => {
    setSlaViolationsPagination({
      current: pagination.current,
      pageSize: pagination.pageSize,
      total: cachedViolations?.length || 0
    });

    if (cachedViolations) {
      const startIndex = (pagination.current - 1) * pagination.pageSize;
      const paginatedData = cachedViolations.slice(startIndex, startIndex + pagination.pageSize);
      setSlaViolations(paginatedData);
    } else {
      fetchSLAViolations(pagination.current);
    }
  };

  const calculateKpiValues = useCallback(() => ({
    totalForms: dashboardStats.totalForms.toString(),
    totalFields: dashboardStats.totalFields.toString(),
    totalSubmissions: dashboardStats.totalSubmissions.toString(),
    filledSubmissions: dashboardStats.filledSubmissions.toString(),
    draftSubmissions: dashboardStats.draftSubmissions.toString(),
    dmaApproved: dashboardStats.dmaApproved.toString(),
    daoApproved: dashboardStats.daoApproved.toString(),
    rdmaApproved: dashboardStats.rdmaApproved.toString(),
    slaCompliance: dashboardStats.slaBreachCount.toString()
  }), [dashboardStats]);

  const kpiData = useMemo(() => calculateKpiValues(), [calculateKpiValues]);

  const displayKPIs = useMemo(() => Object.entries(KPI_CONFIG).map(([key, config]) => ({
    id: key,
    value: kpiData[key] || "0",
    label: language === "EN" ? config.labelEN : config.labelMR,
    color: config.color,
    icon: config.icon,
    isSelected: selectedKpi === key
  })), [kpiData, language, selectedKpi]);
  
  const showDivisionFilter = useMemo(() => isDeskUser || (!isUlbUser && !userDivision && !userDistrict), [userDivision, userDistrict, isUlbUser, isDeskUser]);
  const showDistrictFilter = useMemo(() => isDeskUser || (!isUlbUser && !userDistrict), [userDistrict, isUlbUser, isDeskUser]);
  const showUlbFilter = useMemo(() => isDeskUser || !isUlbUser, [isUlbUser, isDeskUser]);

  // Clear cache when filters change
  useEffect(() => {
    setCachedSubmissions(null);
    setCachedSubmissionsKey('');
    setCachedViolations(null);
    setCachedViolationsKey('');
  }, [selectedDivision, selectedDistrict, ulb, selectedFormId, ulbType, ulbClass]);

  // Initialize component
  useEffect(() => {
    isMounted.current = true;
    const initializeData = async () => {
      await fetchDivisions();
      await fetchAllForms();
    };
    initializeData();
    return () => { isMounted.current = false; };
  }, [fetchDivisions, fetchAllForms]);

  // Handle division changes
  useEffect(() => {
    const divId = division.length > 0 ? division[0] : null;
    if (divId && divId !== 0) {
      setSelectedDivision(divId);
      fetchDistricts(divId);
      setDistrict([]);
      setUlb([]);
      setSelectedDistrict(null);
      setSelectedUlbDetails([]);
      fetchDistrictsForDivision(divId);
    } else {
      setSelectedDivision(null);
      setDistrict([]);
      setUlb([]);
      setDistrictOptions([{ id: 0, name: "All Districts" }]);
      setUlbOptions([{ id: 0, name: "All ULBs" }]);
      setSelectedUlbDetails([]);
      setAllDistrictsForChart([]);
      setAllUlbsForChart([]);
    }
  }, [division, fetchDistricts, fetchDistrictsForDivision]);

  // Handle district changes
  useEffect(() => {
    const distId = district.length > 0 ? district[0] : null;
    if (distId && distId !== 0) {
      setSelectedDistrict(distId);
      fetchUlbs(distId);
      setUlb([]);
      setSelectedUlbDetails([]);
      fetchUlbsForDistrict(distId);
    } else {
      setSelectedDistrict(null);
      setUlb([]);
      setUlbOptions([{ id: 0, name: "All ULBs" }]);
      setSelectedUlbDetails([]);
      setAllUlbsForChart([]);
    }
  }, [district, fetchUlbs, fetchUlbsForDistrict]);

  // Fetch dashboard data when filters change
  useEffect(() => {
    if (!isMounted.current) return;
    const timer = setTimeout(() => fetchDashboardStats(), 1000);
    return () => clearTimeout(timer);
  }, [debouncedDivision, debouncedDistrict, debouncedUlb, ulbType, ulbClass, fetchDashboardStats, selectedFormId]);

  // Update charts when dashboard stats change
  useEffect(() => {
    if (dashboardStats.totalForms > 0 && isMounted.current) updateChartData(dashboardStats);
  }, [dashboardStats, updateChartData]);

  // Forms Modal Columns
  const formsModalColumns = [
    { title: t.formName, dataIndex: 'form_name', key: 'form_name', render: (text, record) => <span style={{ fontWeight: record.submissionCount > 0 ? 'bold' : 'normal' }}>{text}</span> },
    { title: t.status, dataIndex: 'status', key: 'status', render: (status) => <Tag color={status === 'active' ? 'green' : 'default'}>{status}</Tag> },
    {
      title: 'Summary Report',
      key: 'action',
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => fetchSummaryReportData(record)}
        >
          Summary Report
        </Button>
      )
    }
  ];

  const getUlbTypeColor = (type) => {
    if (type === "Municipal Council") return "blue";
    if (type === "Nagar Panchayat") return "green";
    if (type === "Municipal Corporation") return "purple";
    return "default";
  };

  const getUlbClassColor = (ulbClass) => {
    if (ulbClass === "A") return "red";
    if (ulbClass === "B") return "orange";
    if (ulbClass === "C") return "blue";
    if (ulbClass === "NP") return "green";
    return "default";
  };

  return (
    <>
      <Navbar
        onMenuClick={() => {}}
        language={language === "EN" ? "english" : "marathi"}
        onLanguageChange={(lang) => {
          const newLang = lang === "marathi" ? "MR" : "EN";
          setLanguage(newLang);
          sessionStorage.setItem("dma_portal_language", newLang);
        }}
      />

      {/* Forms Modal */}
      <Modal
        title={<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><FileTextOutlined style={{ color: '#1e40af' }} /><span>{t.formsList}</span><Badge count={forms.length} style={{ backgroundColor: '#1e40af' }} /></div>}
        open={formsModalVisible}
        onCancel={() => setFormsModalVisible(false)}
        footer={[<Button key="close" onClick={() => setFormsModalVisible(false)}>{t.close}</Button>]}
        width={800}
      >
        <div style={{ marginBottom: '16px' }}><Text type="secondary">{t.totalFormsCount.replace('{count}', forms.length)} | {t.formsWithSubmissions}: {forms.filter(f => f.submissionCount > 0).length}</Text></div>
        <Table dataSource={forms} columns={formsModalColumns} rowKey="id" size="small" pagination={{ pageSize: 10 }} loading={loadingForms} />
      </Modal>

      {/* Summary Report Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '12px', width: '100%', flexWrap: 'wrap' }}>
            <FileTextOutlined style={{ color: '#1E40AF' }} />
            <span>Summary Report: {selectedFormForSummary?.form_name}</span>
            {!loadingSubmissions && summaryReportData.totalSubmissions?.length > 0 && (
              <Button
                type="primary"
                icon={<PrinterOutlined />}
                onClick={() => {
                  const allData = [];
                  if (summaryReportData.totalSubmissions?.length) allData.push(...summaryReportData.totalSubmissions);
                  if (summaryReportData.filledSubmissions?.length) allData.push(...summaryReportData.filledSubmissions);
                  if (summaryReportData.draftSubmissions?.length) allData.push(...summaryReportData.draftSubmissions);
                  if (summaryReportData.daoApproved?.length) allData.push(...summaryReportData.daoApproved);
                  if (summaryReportData.rdmaApproved?.length) allData.push(...summaryReportData.rdmaApproved);
                  if (summaryReportData.dmaApproved?.length) allData.push(...summaryReportData.dmaApproved);
                  if (summaryReportData.slaBreach?.length) allData.push(...summaryReportData.slaBreach);
                  handlePrintSummarySection('Complete Summary Report', allData);
                }}
                style={{ background: '#8B5CF6', borderRadius: '8px', border: 'none' }}
              >
                Print All Summary
              </Button>
            )}
          </div>
        }
        open={summaryReportModalVisible}
        onCancel={() => {
          setSummaryReportModalVisible(false);
          setLoadingSubmissions(false);
          setSummaryProgress(0);
          setSummaryLoadingStep('');
        }}
        footer={[
          <Button key="close" onClick={() => {
            setSummaryReportModalVisible(false);
            setLoadingSubmissions(false);
            setSummaryProgress(0);
            setSummaryLoadingStep('');
          }}>Close</Button>
        ]}
        width={1200}
      >
        {loadingSubmissions ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 20px',
            minHeight: '450px'
          }}>
            <div style={{ width: '80%', maxWidth: '500px', marginBottom: '30px' }}>
              <div style={{
                backgroundColor: '#f0f0f0',
                borderRadius: '20px',
                overflow: 'hidden',
                boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <div style={{
                  width: `${summaryProgress}%`,
                  height: '12px',
                  backgroundColor: summaryProgress === 100 ? '#10b981' : '#3b82f6',
                  borderRadius: '20px',
                  transition: 'width 0.5s ease-in-out',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                    animation: 'shimmer 1.5s infinite'
                  }} />
                </div>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: '10px',
                fontSize: '14px',
                fontWeight: 'bold'
              }}>
                <span style={{ color: '#666' }}>Progress</span>
                <span style={{ color: summaryProgress === 100 ? '#10b981' : '#3b82f6' }}>
                  {Math.min(100, Math.floor(summaryProgress))}%
                </span>
              </div>
            </div>

            <Spin size="large" />

            <p style={{
              marginTop: '25px',
              fontSize: '16px',
              color: '#555',
              fontWeight: '500'
            }}>
              {summaryLoadingStep || 'Loading summary report...'}
            </p>

            <div style={{ display: 'flex', gap: '8px', marginTop: '15px' }}>
              <span className="dot" style={{ animationDelay: '0s' }}>.</span>
              <span className="dot" style={{ animationDelay: '0.2s' }}>.</span>
              <span className="dot" style={{ animationDelay: '0.4s' }}>.</span>
            </div>
          </div>
        ) : (
          <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {/* Total Submissions Card */}
            {summaryReportData.totalSubmissions && summaryReportData.totalSubmissions.length > 0 && (
              <Card
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span><CloudUploadOutlined style={{ color: '#10b981', marginRight: '8px' }} />Total Submissions ({summaryReportData.totalSubmissions.length})</span>
                    <Button
                      size="small"
                      icon={<PrinterOutlined />}
                      onClick={() => handlePrintSummarySection('Total Submissions', summaryReportData.totalSubmissions)}
                      style={{ background: '#10b981', color: 'white', borderRadius: '6px' }}
                    >
                      Print
                    </Button>
                  </div>
                }
                style={{ marginBottom: '20px', borderRadius: '12px' }}
              >
                <Table
                  dataSource={summaryReportData.totalSubmissions}
                  columns={[
                    { title: 'ULB Name', dataIndex: 'ulb_name', key: 'ulb_name', width: 250 },
                    { title: 'District', dataIndex: 'district_name', key: 'district_name', width: 180 },
                    { title: 'Division', dataIndex: 'division_name', key: 'division_name', width: 180 },
                    { title: 'Status', dataIndex: 'status', key: 'status', width: 150 },
                    { title: 'Submitted On', dataIndex: 'submitted_on', key: 'submitted_on', width: 200, render: (date) => date ? new Date(date).toLocaleString() : '-' }
                  ]}
                  rowKey={(record) => record.id || Math.random().toString()}
                  size="small"
                  pagination={{ pageSize: 5 }}
                  scroll={{ x: 1000 }}
                />
              </Card>
            )}

            {/* Submitted by ULB Card */}
            {summaryReportData.filledSubmissions && summaryReportData.filledSubmissions.length > 0 && (
              <Card
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span><CheckCircleOutlined style={{ color: '#22c55e', marginRight: '8px' }} />Submitted by ULB ({summaryReportData.filledSubmissions.length})</span>
                    <Button
                      size="small"
                      icon={<PrinterOutlined />}
                      onClick={() => handlePrintSummarySection('Submitted by ULB', summaryReportData.filledSubmissions)}
                      style={{ background: '#22c55e', color: 'white', borderRadius: '6px' }}
                    >
                      Print
                    </Button>
                  </div>
                }
                style={{ marginBottom: '20px', borderRadius: '12px' }}
              >
                <Table
                  dataSource={summaryReportData.filledSubmissions}
                  columns={[
                    { title: 'ULB Name', dataIndex: 'ulb_name', key: 'ulb_name', width: 250 },
                    { title: 'District', dataIndex: 'district_name', key: 'district_name', width: 180 },
                    { title: 'Division', dataIndex: 'division_name', key: 'division_name', width: 180 },
                    { title: 'Status', dataIndex: 'status', key: 'status', width: 150 },
                    { title: 'Submitted On', dataIndex: 'submitted_on', key: 'submitted_on', width: 200, render: (date) => date ? new Date(date).toLocaleString() : '-' }
                  ]}
                  rowKey={(record) => record.id || Math.random().toString()}
                  size="small"
                  pagination={{ pageSize: 5 }}
                  scroll={{ x: 1000 }}
                />
              </Card>
            )}

            {/* Pending ULBs Submission Card */}
            {summaryReportData.draftSubmissions && summaryReportData.draftSubmissions.length > 0 && (
              <Card
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span><ClockCircleOutlined style={{ color: '#f59e0b', marginRight: '8px' }} />Pending ULBs Submission ({summaryReportData.draftSubmissions.length})</span>
                    <Button
                      size="small"
                      icon={<PrinterOutlined />}
                      onClick={() => handlePrintSummarySection('Pending ULBs Submission', summaryReportData.draftSubmissions)}
                      style={{ background: '#f59e0b', color: 'white', borderRadius: '6px' }}
                    >
                      Print
                    </Button>
                  </div>
                }
                style={{ marginBottom: '20px', borderRadius: '12px' }}
              >
                <Table
                  dataSource={summaryReportData.draftSubmissions}
                  columns={[
                    { title: 'ULB Name', dataIndex: 'ulb_name', key: 'ulb_name', width: 250 },
                    { title: 'District', dataIndex: 'district_name', key: 'district_name', width: 180 },
                    { title: 'Division', dataIndex: 'division_name', key: 'division_name', width: 180 },
                    { title: 'Status', dataIndex: 'status', key: 'status', width: 150 },
                    { title: 'Submitted On', dataIndex: 'submitted_on', key: 'submitted_on', width: 200, render: (date) => date ? new Date(date).toLocaleString() : '-' }
                  ]}
                  rowKey={(record) => record.id || Math.random().toString()}
                  size="small"
                  pagination={{ pageSize: 5 }}
                  scroll={{ x: 1000 }}
                />
              </Card>
            )}

            {/* DAO Approved Card */}
            {summaryReportData.daoApproved && summaryReportData.daoApproved.length > 0 && (
              <Card
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span><CheckCircleOutlined style={{ color: '#8b5cf6', marginRight: '8px' }} />DAO Approved ({summaryReportData.daoApproved.length})</span>
                    <Button
                      size="small"
                      icon={<PrinterOutlined />}
                      onClick={() => handlePrintSummarySection('DAO Approved', summaryReportData.daoApproved)}
                      style={{ background: '#8b5cf6', color: 'white', borderRadius: '6px' }}
                    >
                      Print
                    </Button>
                  </div>
                }
                style={{ marginBottom: '20px', borderRadius: '12px' }}
              >
                <Table
                  dataSource={summaryReportData.daoApproved}
                  columns={[
                    { title: 'ULB Name', dataIndex: 'ulb_name', key: 'ulb_name', width: 250 },
                    { title: 'District', dataIndex: 'district_name', key: 'district_name', width: 180 },
                    { title: 'Division', dataIndex: 'division_name', key: 'division_name', width: 180 },
                    { title: 'Status', dataIndex: 'status', key: 'status', width: 150 },
                    { title: 'Submitted On', dataIndex: 'submitted_on', key: 'submitted_on', width: 200, render: (date) => date ? new Date(date).toLocaleString() : '-' }
                  ]}
                  rowKey={(record) => record.id || Math.random().toString()}
                  size="small"
                  pagination={{ pageSize: 5 }}
                  scroll={{ x: 1000 }}
                />
              </Card>
            )}

            {/* RDMA Approved Card */}
            {summaryReportData.rdmaApproved && summaryReportData.rdmaApproved.length > 0 && (
              <Card
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span><TrophyOutlined style={{ color: '#ec4899', marginRight: '8px' }} />RDMA Approved ({summaryReportData.rdmaApproved.length})</span>
                    <Button
                      size="small"
                      icon={<PrinterOutlined />}
                      onClick={() => handlePrintSummarySection('RDMA Approved', summaryReportData.rdmaApproved)}
                      style={{ background: '#ec4899', color: 'white', borderRadius: '6px' }}
                    >
                      Print
                    </Button>
                  </div>
                }
                style={{ marginBottom: '20px', borderRadius: '12px' }}
              >
                <Table
                  dataSource={summaryReportData.rdmaApproved}
                  columns={[
                    { title: 'ULB Name', dataIndex: 'ulb_name', key: 'ulb_name', width: 250 },
                    { title: 'District', dataIndex: 'district_name', key: 'district_name', width: 180 },
                    { title: 'Division', dataIndex: 'division_name', key: 'division_name', width: 180 },
                    { title: 'Status', dataIndex: 'status', key: 'status', width: 150 },
                    { title: 'Submitted On', dataIndex: 'submitted_on', key: 'submitted_on', width: 200, render: (date) => date ? new Date(date).toLocaleString() : '-' }
                  ]}
                  rowKey={(record) => record.id || Math.random().toString()}
                  size="small"
                  pagination={{ pageSize: 5 }}
                  scroll={{ x: 1000 }}
                />
              </Card>
            )}

            {/* DMA Approved Card */}
            {summaryReportData.dmaApproved && summaryReportData.dmaApproved.length > 0 && (
              <Card
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span><CheckCircleOutlined style={{ color: '#3b82f6', marginRight: '8px' }} />DMA Approved ({summaryReportData.dmaApproved.length})</span>
                    <Button
                      size="small"
                      icon={<PrinterOutlined />}
                      onClick={() => handlePrintSummarySection('DMA Approved', summaryReportData.dmaApproved)}
                      style={{ background: '#3b82f6', color: 'white', borderRadius: '6px' }}
                    >
                      Print
                    </Button>
                  </div>
                }
                style={{ marginBottom: '20px', borderRadius: '12px' }}
              >
                <Table
                  dataSource={summaryReportData.dmaApproved}
                  columns={[
                    { title: 'ULB Name', dataIndex: 'ulb_name', key: 'ulb_name', width: 250 },
                    { title: 'District', dataIndex: 'district_name', key: 'district_name', width: 180 },
                    { title: 'Division', dataIndex: 'division_name', key: 'division_name', width: 180 },
                    { title: 'Status', dataIndex: 'status', key: 'status', width: 150 },
                    { title: 'Submitted On', dataIndex: 'submitted_on', key: 'submitted_on', width: 200, render: (date) => date ? new Date(date).toLocaleString() : '-' }
                  ]}
                  rowKey={(record) => record.id || Math.random().toString()}
                  size="small"
                  pagination={{ pageSize: 5 }}
                  scroll={{ x: 1000 }}
                />
              </Card>
            )}

            {/* SLA Breach Card */}
            {summaryReportData.slaBreach && summaryReportData.slaBreach.length > 0 && (
              <Card
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span><WarningOutlined style={{ color: '#ef4444', marginRight: '8px' }} />SLA Breach ({summaryReportData.slaBreach.length})</span>
                    <Button
                      size="small"
                      icon={<PrinterOutlined />}
                      onClick={() => handlePrintSummarySection('SLA Breach', summaryReportData.slaBreach)}
                      style={{ background: '#ef4444', color: 'white', borderRadius: '6px' }}
                    >
                      Print
                    </Button>
                  </div>
                }
                style={{ marginBottom: '20px', borderRadius: '12px' }}
              >
                <Table
                  dataSource={summaryReportData.slaBreach}
                  columns={[
                    { title: 'ULB Name', dataIndex: 'ulb_name', key: 'ulb_name', width: 250 },
                    { title: 'District', dataIndex: 'district_name', key: 'district_name', width: 180 },
                    { title: 'Division', dataIndex: 'division_name', key: 'division_name', width: 180 },
                    { title: 'Status', dataIndex: 'status', key: 'status', width: 150 },
                    { title: 'Violated On', dataIndex: 'submitted_on', key: 'submitted_on', width: 200, render: (date) => date ? new Date(date).toLocaleString() : '-' }
                  ]}
                  rowKey={(record) => record.id || Math.random().toString()}
                  size="small"
                  pagination={{ pageSize: 5 }}
                  scroll={{ x: 1000 }}
                />
              </Card>
            )}

            {/* No Data Message */}
            {(!summaryReportData.totalSubmissions || summaryReportData.totalSubmissions.length === 0) &&
             (!summaryReportData.filledSubmissions || summaryReportData.filledSubmissions.length === 0) &&
             (!summaryReportData.draftSubmissions || summaryReportData.draftSubmissions.length === 0) && (
              <Empty
                description="No data found for the selected filters"
                style={{ padding: '50px' }}
              />
            )}
          </div>
        )}
      </Modal>

      {/* Submissions Modal */}
      <Modal
        title={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {selectedKpi === "filledSubmissions" && <CloudUploadOutlined style={{ color: '#22c55e' }} />}
            {selectedKpi === "draftSubmissions" && <ClockCircleOutlined style={{ color: '#f59e0b' }} />}
            {selectedKpi === "dmaApproved" && <CheckCircleOutlined style={{ color: '#3b82f6' }} />}
            {selectedKpi === "rdmaApproved" && <TrophyOutlined style={{ color: '#8b5cf6' }} />}
            {selectedKpi === "totalSubmissions" && <FileTextOutlined style={{ color: '#10b981' }} />}
            {selectedKpi === "daoApproved" && <CheckCircleOutlined style={{ color: '#8b5cf6' }} />}
            <span>
              {selectedKpi === "filledSubmissions" && "Filled Submissions"}
              {selectedKpi === "draftSubmissions" && "Pending ULBs Submissions"}
              {selectedKpi === "dmaApproved" && "DMA Approved Submissions"}
              {selectedKpi === "rdmaApproved" && "RDMA Approved Submissions"}
              {selectedKpi === "totalSubmissions" && "All Submissions"}
              {selectedKpi === "daoApproved" && "DAO Approved Submissions"}
            </span>
          </div>
        </div>}
        open={submissionsModalVisible}
        onCancel={() => setSubmissionsModalVisible(false)}
        footer={[
          <Button key="print" icon={<PrinterOutlined />} onClick={handlePrint} style={{ background: '#1e40af', color: 'white' }}>
            Print
          </Button>,
          <Button key="close" onClick={() => setSubmissionsModalVisible(false)}>{t.close}</Button>
        ]}
        width={1200}
      >
        <Table
          dataSource={submissionsData}
          columns={[
            { title: 'Form Name', dataIndex: 'form_name', key: 'form_name', width: 250, render: (text) => text || '-' },
            { title: 'ULB Name', dataIndex: 'ulb_name', key: 'ulb_name', width: 250, fixed: 'left', render: (text) => <strong style={{ color: selectedKpi === 'daoApproved' ? '#8b5cf6' : selectedKpi === 'rdmaApproved' ? '#ec4899' : '#1e40af' }}>{text || '-'}</strong> },
            { title: 'District', dataIndex: 'district_name', key: 'district_name', width: 180, render: (text) => text || '-' },
            { title: 'Division', dataIndex: 'division_name', key: 'division_name', width: 180, render: (text) => text || '-' },
          ]}
          rowKey={(record) => record.id || record.submission_id || Math.random().toString()}
          size="small"
          pagination={{
            current: submissionsPagination.current,
            pageSize: submissionsPagination.pageSize,
            total: submissionsPagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} submissions`
          }}
          onChange={handleSubmissionsTableChange}
          loading={loadingSubmissions}
          locale={{ emptyText: t.noSubmissions }}
          scroll={{ x: 1200 }}
        />
      </Modal>

      {/* SLA Violations Modal */}
      <Modal
        title={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <WarningOutlined style={{ color: '#ef4444' }} />
            <span>Escalated Submissions (SLA Breach)</span>
          </div>
        </div>}
        open={slaViolationsModalVisible}
        onCancel={() => setSlaViolationsModalVisible(false)}
        footer={[
          <Button key="print" icon={<PrinterOutlined />} onClick={handlePrint} style={{ background: '#1e40af', color: 'white' }}>
            Print
          </Button>,
          <Button key="close" onClick={() => setSlaViolationsModalVisible(false)}>Close</Button>
        ]}
        width={1200}
      >
        <Table
          dataSource={slaViolations}
          columns={[
            { title: 'Form Name', dataIndex: 'form_name', key: 'form_name', width: 200, render: (text) => text || '-' },
            { title: 'ULB Name', dataIndex: 'ulb_name', key: 'ulb_name', width: 250, fixed: 'left', render: (text) => <strong style={{ color: '#1e40af' }}>{text || '-'}</strong> },
            { title: 'District', dataIndex: 'district_name', key: 'district_name', width: 150, render: (text) => text || '-' },
            { title: 'Division', dataIndex: 'division_name', key: 'division_name', width: 150, render: (text) => text || '-' },
            { title: 'Violated At', dataIndex: 'violated_at', key: 'violated_at', width: 200, render: (date) => date ? new Date(date).toLocaleString() : '-' }
          ]}
          rowKey={(record, index) => record.id || record.submission_id || index}
          size="middle"
          pagination={{
            current: slaViolationsPagination.current,
            pageSize: slaViolationsPagination.pageSize,
            total: slaViolationsPagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `Total ${total} escalated submissions`
          }}
          onChange={handleSlaViolationsTableChange}
          loading={loadingSlaViolations}
          locale={{ emptyText: 'No escalated submissions found' }}
          scroll={{ x: 1200 }}
        />
      </Modal>

      {/* MAIN CONTENT */}
      <div className="dashboard-full-wrapper">
        <Content className="dashboard-content-full">
          <div className="dashboard-header-full">
            <div className="dashboard-title-full">
              <h1>{t.dashboardTitle}</h1>
            </div>
          </div>

          {/* HORIZONTAL FILTER BAR */}
          <FloatingCard className="horizontal-filters-bar" delay={0.05} style={{ background: 'white', borderRadius: '20px', padding: '20px 24px', marginBottom: '28px', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)', border: '1px solid #eef2f6', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg, #3b82f6, #8b5cf6, #10b981, #f59e0b, #ef4444)', borderRadius: '20px 20px 0 0' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FilterOutlined style={{ fontSize: '20px', color: '#3b82f6', padding: '6px', background: '#eff6ff', borderRadius: '12px' }} />
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>{t.filters}</h3>
                <Tag color="blue" style={{ fontSize: '11px' }}>{t.autoApply}</Tag>
              </div>
              <Button icon={<ReloadOutlined />} onClick={handleReset} disabled={loadingDivision || loadingDistrict || loadingUlb} style={{ background: '#ef4444', border: 'none', color: 'white', fontWeight: 500, borderRadius: '12px', padding: '6px 20px', height: '38px' }}>{t.resetAllFilters}</Button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-end' }}>
              {showDivisionFilter && (
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <div style={{ marginBottom: '8px' }}><span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: 700, color: '#475569', background: '#f8fafc', padding: '4px 12px', borderRadius: '20px' }}><BuildOutlined style={{ fontSize: '12px' }} />{t.division}</span></div>
                  <Select ref={divisionSelectRef} mode="multiple" placeholder={t.selectDivision} value={division} onChange={(value) => handleSelectChange(setDivision, value, divisionSelectRef)} style={{ width: '100%' }} loading={loadingDivision} disabled={loadingDivision} dropdownMatchSelectWidth={false} autoClearSearchValue listHeight={200}>
                    {divisionOptions.map(opt => (<Option key={opt.id} value={opt.id} onClick={() => handleOptionClick(divisionSelectRef)}><span>{opt.name}</span></Option>))}
                  </Select>
                </div>
              )}
              {showDistrictFilter && (
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <div style={{ marginBottom: '8px' }}><span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: 700, color: '#475569', background: '#f8fafc', padding: '4px 12px', borderRadius: '20px' }}><FlagOutlined style={{ fontSize: '12px' }} />{t.district}</span></div>
                  <Select ref={districtSelectRef} mode="multiple" placeholder={!selectedDivision ? t.selectDivision : t.selectDistrict} value={district} onChange={(value) => handleSelectChange(setDistrict, value, districtSelectRef)} style={{ width: '100%' }} loading={loadingDistrict} disabled={!selectedDivision || loadingDistrict} dropdownMatchSelectWidth={false} autoClearSearchValue listHeight={200}>
                    {districtOptions.map(opt => (<Option key={opt.id} value={opt.id} onClick={() => handleOptionClick(districtSelectRef)}><span>{opt.name}</span></Option>))}
                  </Select>
                </div>
              )}
              {showUlbFilter && (
                <div style={{ flex: 1, minWidth: '220px' }}>
                  <div style={{ marginBottom: '8px' }}><span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: 700, color: '#475569', background: '#f8fafc', padding: '4px 12px', borderRadius: '20px' }}><DashboardOutlined style={{ fontSize: '12px' }} />{t.ulbs}</span></div>
                  <Select ref={ulbSelectRef} mode="multiple" placeholder={!selectedDistrict && !userDistrict ? t.selectDistrict : t.selectULB} value={ulb} onChange={handleUlbSelect} style={{ width: '100%' }} loading={loadingUlb} disabled={(!selectedDistrict && !userDistrict) || loadingUlb} dropdownMatchSelectWidth={false} autoClearSearchValue listHeight={200}>
                    {ulbOptions.map(opt => (<Option key={opt.id} value={opt.id} onClick={() => handleOptionClick(ulbSelectRef)}><span>{opt.name}</span></Option>))}
                  </Select>
                  {ulbError && <div style={{ marginTop: '8px', padding: '6px 12px', background: '#fef2e8', borderLeft: '3px solid #f59e0b', borderRadius: '8px', fontSize: '11px', color: '#92400e', display: 'flex', alignItems: 'center', gap: '8px' }}><WarningOutlined style={{ fontSize: '12px', color: '#f59e0b' }} /><span>{ulbError}</span><CloseOutlined style={{ fontSize: '10px', marginLeft: 'auto', cursor: 'pointer' }} onClick={() => setUlbError(null)} /></div>}
                </div>
              )}
              <div style={{ flex: 1, minWidth: '220px' }}>
                <div style={{ marginBottom: '8px' }}><span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: 700, color: '#475569', background: '#f8fafc', padding: '4px 12px', borderRadius: '20px' }}><FileTextOutlined style={{ fontSize: '12px' }} />{t.forms}</span></div>
                <Select ref={formSelectRef} placeholder={t.selectForm} value={selectedFormId || undefined} onChange={handleFormSelect} style={{ width: '100%' }} dropdownMatchSelectWidth={false} listHeight={200} showSearch allowClear filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}>
                  <Option value={0} key="none"><span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#ef4444', fontWeight: 500 }}><CloseOutlined /> Clear Selection</span></Option>
                  {forms.map((form) => (<Option key={form.id} value={form.id}><span>{form.form_name}</span></Option>))}
                </Select>
              </div>
            </div>
            {(division.length > 0 || district.length > 0 || ulb.length > 0 || ulbType !== "all" || ulbClass !== "all" || selectedFormId) && (
              <div style={{ marginTop: '18px', paddingTop: '14px', borderTop: '1px dashed #e2e8f0', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', background: '#f8fafc', padding: '12px 16px', borderRadius: '12px', marginTop: '16px' }}>
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Active Filters:</span>
                {division.length > 0 && division[0] !== 0 && <Tag closable onClose={() => setDivision([])} color="purple">{t.division}: {divisionOptions.find(d => division.includes(d.id))?.name}</Tag>}
                {district.length > 0 && district[0] !== 0 && <Tag closable onClose={() => setDistrict([])} color="cyan">{t.district}: {districtOptions.find(d => district.includes(d.id))?.name}</Tag>}
                {ulb.length > 0 && ulb[0] !== 0 && <Tag closable onClose={() => setUlb([])} color="geekblue">{t.ulbs}: {ulb.length} selected</Tag>}
                {ulbType && ulbType !== "all" && <Tag closable onClose={() => setUlbType("all")} color="gold">{t.ulbType}: {ulbType}</Tag>}
                {ulbClass && ulbClass !== "all" && <Tag closable onClose={() => setUlbClass("all")} color="orange">{t.ulbClass}: Class {ulbClass}</Tag>}
                {selectedForm && <Tag closable onClose={() => handleFormSelect(0)} color="blue">{t.forms}: {selectedForm.form_name}</Tag>}
              </div>
            )}
          </FloatingCard>

          {/* KPI SECTION */}
          <div className="full-width-dashboard">
            <div className="kpi-section-full">
             <div className="kpi-grid-full">
              {displayKPIs.map((kpi, i) => (
                <FloatingCard
                  key={i}
                  delay={0.1 + i * 0.02}
                  className={`kpi-card ${kpi.isSelected ? 'selected' : ''}`}
                  onClick={() => handleKpiClick(kpi.id)}
                  style={{ borderTop: `3px solid ${kpi.color}` }}
                >
                  <div className="kpi-icon" style={{ color: kpi.color }}>{kpi.icon}</div>
                  <div className="kpi-value"><AnimatedNumber value={parseInt(kpi.value.replace(/,/g, '')) || 0} /></div>
                  <div className="kpi-label">{kpi.label}</div>
                </FloatingCard>
              ))}
            </div>
            </div>
          </div>
        </Content>
      </div>

      <style jsx>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .dashboard-full-wrapper { margin-left: 0; padding-top: 85px; min-height: 100vh; background: #f8fafc; width: 100%; }
        .dashboard-content-full { padding: 24px; background: transparent; min-height: calc(100vh - 85px - 32px); animation: fadeSlideUp 0.6s ease-out; max-width: 1600px; margin: 0 auto; }
        .dashboard-header-full { margin-bottom: 24px; }
        .dashboard-title-full h1 { margin: 0; font-size: 28px; font-weight: 700; background: linear-gradient(135deg, #1e293b 0%, #3b82f6 100%); -webkit-background-clip: text; background-clip: text; color: transparent; }
        .kpi-section-full { margin-bottom: 24px; }
        .kpi-grid-full { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
        .kpi-card { background: white; border-radius: 16px; padding: 20px 16px; text-align: center; box-shadow: 0 1px 4px rgba(0,0,0,0.04); border: 1px solid #eef2f6; transition: all 0.3s ease; }
        .kpi-card:not(.disabled):hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(0,0,0,0.08); cursor: pointer; }
        .kpi-card.disabled { opacity: 0.6; cursor: not-allowed; }
        .kpi-card.disabled:hover { transform: none; box-shadow: 0 1px 4px rgba(0,0,0,0.04); }
        .kpi-card.selected { border: 2px solid #3b82f6; background: #f0f9ff; }
        .kpi-icon { font-size: 32px; margin-bottom: 12px; }
        .kpi-value { font-size: 32px; font-weight: 800; color: #0f172a; margin-bottom: 8px; }
        .kpi-label { font-size: 14px; font-weight: 500; color: #475569; }
        .dot {
          font-size: 24px;
          font-weight: bold;
          animation: pulse 1.5s ease-in-out infinite;
          display: inline-block;
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.2; }
          50% { opacity: 1; }
        }
        @media (max-width: 1200px) { .kpi-grid-full { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 768px) { .dashboard-full-wrapper { padding-top: 70px; } .dashboard-content-full { padding: 16px; } .kpi-grid-full { grid-template-columns: 1fr; } .dashboard-title-full h1 { font-size: 22px; } }
      `}</style>
    </>
  );
};

export default Dashboard;